import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { FaAlignLeft, FaAlignCenter, FaAlignRight, FaAlignJustify, FaYoutube, FaListUl, FaListOl, FaCheckSquare } from 'react-icons/fa';
import { LuLink, LuUnlink, LuVideo, LuImage, LuUpload, LuX, LuSettings, LuTrash2 } from 'react-icons/lu';
import DraggableToolbar from './DraggableToolbar';
import { isVideo, getEmbedUrl } from '../utils/helpers';
import { MediaInput } from './MediaInput';
import { resolveMediaUrl, resolveHtmlMediaUrls, getAppBase, getTenantFullUrl, normalizeAppUrl, getApiUrl, authHeaders, getTenantStorageKey, isEmptyHtml, isFileUrl } from '../utils/styleHelpers.js';
import { uploadMediaFile, IOS_FILE_INPUT_STYLE } from '../utils/imageUploadHelper.js';
import GalleryVideoItem from './GalleryVideoItem';
import { FONT_OPTIONS, getFontOptions } from '../config/fontOptions.js';
import MediaLibraryModal from './MediaLibraryModal.jsx';

const cleanAllLinks = (htmlString) => {
    if (!htmlString) return '';
    if (typeof htmlString !== 'string') return htmlString;
    if (isEmptyHtml(htmlString)) return '';
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, 'text/html');
        const links = doc.querySelectorAll('a');
        links.forEach(link => {
            let href = link.getAttribute('href');
            if (href) {
                let newHref = href.trim();
                const origin = typeof window !== 'undefined' ? window.location.origin : '';
                if (origin && newHref.startsWith(origin)) {
                    newHref = newHref.substring(origin.length);
                }

                // Strip hash routing remnants (e.g. /#/pages/ecommerce or #/pages/ecommerce -> /pages/ecommerce)
                const isHashRoute = /^\/?#\//.test(newHref);
                if (isHashRoute) {
                    newHref = newHref.replace(/^\/?#\//, '/');
                }
                
                if (newHref === '') newHref = '/';

                const isFile = isFileUrl(newHref);
                const isAnchor = newHref.startsWith('#');
                const isProtocol = /^(https?:\/\/|mailto:|tel:|javascript:)/i.test(newHref);
                const isSlashRelative = newHref.startsWith('/');

                if (!isAnchor && !isProtocol && !isSlashRelative) {
                    // Prepend https:// only to domain names with periods (and not files), else treat as internal relative route
                    if (newHref.includes('.') && !newHref.startsWith('.') && !isFile) {
                        newHref = 'https://' + newHref;
                    } else {
                        newHref = '/' + newHref;
                    }
                }

                const isExternal = newHref.startsWith('http://') || newHref.startsWith('https://');
                const isMailOrTel = newHref.startsWith('mailto:') || newHref.startsWith('tel:');
                const target = link.getAttribute('target');

                if (isFile) {
                    const fullHref = resolveMediaUrl(newHref);
                    link.setAttribute('href', fullHref);
                    link.setAttribute('target', '_blank');
                    link.setAttribute('rel', 'noopener noreferrer');
                } else if (!isExternal && !isMailOrTel && !isAnchor) {
                    const fullHref = getTenantFullUrl(newHref);
                    link.setAttribute('href', fullHref);
                    if (target !== '_blank') {
                        link.removeAttribute('target');
                        link.removeAttribute('rel');
                    } else {
                        link.setAttribute('rel', 'noopener noreferrer');
                    }
                } else if (isExternal || target === '_blank') {
                    link.setAttribute('href', newHref);
                    link.setAttribute('target', '_blank');
                    link.setAttribute('rel', 'noopener noreferrer');
                } else {
                    link.setAttribute('href', newHref);
                    link.removeAttribute('target');
                    link.removeAttribute('rel');
                }
                const linkTextClean = (link.textContent || '').trim().toUpperCase();
                const innerImg = link.querySelector('img');
                const isImageOnlyLink = !linkTextClean || (innerImg && (linkTextClean === (innerImg.getAttribute('alt') || '').trim().toUpperCase()));
                
                if (linkTextClean === 'HERE' || linkTextClean === 'CLICK HERE' || linkTextClean === 'LINK' || isImageOnlyLink) {
                    const targetHref = href || link.getAttribute('href') || '';
                    const cleanPath = targetHref.replace(/^https?:\/\/[^\/]+/i, '').replace(/^\//, '');
                    const domainHost = targetHref.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0];
                    const labelName = cleanPath ? cleanPath.replace(/[-_]/g, ' ') : (domainHost || 'page details');
                    const imgAlt = innerImg ? (innerImg.getAttribute('alt') || '').trim() : '';
                    const descriptiveLabel = (imgAlt && imgAlt !== '') ? imgAlt : `Visit ${labelName}`;

                    if (!link.getAttribute('aria-label')) {
                        link.setAttribute('aria-label', descriptiveLabel);
                    }
                    if (!link.getAttribute('title')) {
                        link.setAttribute('title', descriptiveLabel);
                    }
                    if (innerImg && (!imgAlt || imgAlt === '')) {
                        innerImg.setAttribute('alt', descriptiveLabel);
                    }
                }
            }
        });

        // Enforce alt, loading, decoding, and explicit layout dimensions on all image elements for accessibility & CLS compliance
        const imgs = doc.querySelectorAll('img');
        imgs.forEach(img => {
            if (!img.hasAttribute('alt')) {
                img.setAttribute('alt', '');
            }
            if (!img.hasAttribute('loading')) {
                img.setAttribute('loading', 'lazy');
            }
            if (!img.hasAttribute('decoding')) {
                img.setAttribute('decoding', 'async');
            }
            if (!img.hasAttribute('width') && !img.hasAttribute('height')) {
                img.setAttribute('width', '800');
                img.setAttribute('height', '600');
            }
        });

        return doc.body.innerHTML;
    } catch (e) {
        console.error("cleanAllLinks failed:", e);
        return htmlString;
    }
};

const PLACEHOLDERS = [
    'New Section',
    'New Section Heading',
    'Add your subtitle here...',
    'New Column',
    'Add text here...',
    'New Image',
    'Add description...',
    'Enter text here...',
    'Enter product description here...',
    'Product description...',
    'Photo Gallery',
    'Sunny Beach',
    'Mountains',
    'Forest',
    'A beautiful scene.',
    'Peaks at sunrise.',
    'Lush canopy.'
];

const isPlaceholderText = (text) => {
    if (!text) return false;
    const clean = text.trim().toLowerCase();
    return PLACEHOLDERS.some(p => p.toLowerCase() === clean);
};

const unwrapSingleParagraph = (html, tag) => {
    if (!html || typeof html !== 'string') return html;
    const trimmed = html.trim();
    if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span', 'strong', 'em', 'label', 'a'].includes(tag)) {
        const pMatch = trimmed.match(/^<p\b[^>]*>([\s\S]*?)<\/p>$/i);
        if (pMatch && !pMatch[1].includes('<p') && !pMatch[1].includes('</p>')) {
            return pMatch[1];
        }
        const divMatch = trimmed.match(/^<div\b[^>]*>([\s\S]*?)<\/div>$/i);
        if (divMatch && !divMatch[1].includes('<div') && !divMatch[1].includes('</div') && !divMatch[1].includes('<p')) {
            return divMatch[1];
        }
    }
    return html;
};

export const Editable = ({ as: Tag = 'div', value, onChange, isEditing, type = 'text', isDashboard = false, style, onStyleChange, currentStyles, className, imageLink, imageLinkNewTab, onImageLinkChange, ...props }) => {
    const fontOptions = getFontOptions(props?.customFonts || props?.settings?.customFonts);
    const editorRef = useRef(null);
    const savedRangeRef = useRef(null);
    const savedOffsetRef = useRef(null);
    const containerRef = useRef(null);
    const isTypingRef = useRef(false);
    const isRichText = type === 'richtext';
    const [isActive, setIsActive] = useState(false);
    // Link modal state for Formatting Tools
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const [linkText, setLinkText] = useState('');
    const [linkNewTab, setLinkNewTab] = useState(true);
    const [isEditingExistingLink, setIsEditingExistingLink] = useState(false);
    const [isImageLinkMode, setIsImageLinkMode] = useState(false);
    const activeLinkNodeRef = useRef(null);
    const selectedImgNodeRef = useRef(null);
    const savedSelectionInfoRef = useRef(null);
    // Local preview for image type: shows blob URL immediately after file selection
    const [localPreviewSrc, setLocalPreviewSrc] = useState(null);
    const localPreviewRef = useRef(null); // stores the blob URL so we can revoke it
    // Video modal & inspector state for Rich Text formatting tools
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [videoInputUrl, setVideoInputUrl] = useState('');
    const [videoLoopSetting, setVideoLoopSetting] = useState(true);
    const [videoAutoplaySetting, setVideoAutoplaySetting] = useState(false);
    const [videoControlsSetting, setVideoControlsSetting] = useState(true);
    const [isEditingExistingVideo, setIsEditingExistingVideo] = useState(false);
    const activeVideoNodeRef = useRef(null);
    const [showMediaLibraryModal, setShowMediaLibraryModal] = useState(false);
    const [selectedVideoInfo, setSelectedVideoInfo] = useState(null);

    // Helper to calculate exact character offset within contentEditable
    const getSelectionOffsets = () => {
        if (!editorRef.current) return null;
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return null;
        const range = sel.getRangeAt(0);
        const container = range.commonAncestorContainer.nodeType === Node.TEXT_NODE 
            ? range.commonAncestorContainer.parentNode 
            : range.commonAncestorContainer;
        if (!editorRef.current.contains(container) && editorRef.current !== container) return null;

        try {
            const preCaretRange = range.cloneRange();
            preCaretRange.selectNodeContents(editorRef.current);
            preCaretRange.setEnd(range.startContainer, range.startOffset);
            const start = preCaretRange.toString().length;
            const end = start + range.toString().length;
            return { start, end };
        } catch (e) {
            return null;
        }
    };

    // Helper to restore exact character offset across DOM re-renders
    const restoreSelectionOffsets = (offsets) => {
        if (!editorRef.current || !offsets) return;
        const { start, end } = offsets;
        const sel = window.getSelection();
        if (!sel) return;

        let charCount = 0;
        let startNode = null, startOffset = 0;
        let endNode = null, endOffset = 0;
        let foundStart = false, foundEnd = false;

        const traverse = (node) => {
            if (foundStart && foundEnd) return;
            if (node.nodeType === Node.TEXT_NODE) {
                const nextCharCount = charCount + node.length;
                if (!foundStart && start >= charCount && start <= nextCharCount) {
                    startNode = node;
                    startOffset = start - charCount;
                    foundStart = true;
                }
                if (!foundEnd && end >= charCount && end <= nextCharCount) {
                    endNode = node;
                    endOffset = end - charCount;
                    foundEnd = true;
                }
                charCount = nextCharCount;
            } else {
                for (let i = 0; i < node.childNodes.length; i++) {
                    traverse(node.childNodes[i]);
                }
            }
        };

        traverse(editorRef.current);

        if (startNode) {
            try {
                const range = document.createRange();
                range.setStart(startNode, startOffset);
                range.setEnd(endNode || startNode, endNode ? endOffset : startOffset);
                sel.removeAllRanges();
                sel.addRange(range);
                savedRangeRef.current = range.cloneRange();
            } catch (e) {
                console.error("restoreSelectionOffsets error:", e);
            }
        }
    };

    // Keeps the current caret or active editing selection comfortably visible on desktop, iOS, and Android
    const keepEditingPointInView = (options = {}) => {
        const { smooth = false } = options;
        if (typeof window === 'undefined' || !isEditing) return;

        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || !editorRef.current) return;

        try {
            const range = sel.getRangeAt(0);
            const container = range.commonAncestorContainer.nodeType === Node.TEXT_NODE
                ? range.commonAncestorContainer.parentNode
                : range.commonAncestorContainer;
            if (!editorRef.current.contains(container) && editorRef.current !== container) return;

            let rect = null;
            const rects = range.getClientRects();
            if (rects && rects.length > 0) {
                rect = rects[0];
            } else {
                rect = range.getBoundingClientRect();
            }

            if (!rect || (rect.top === 0 && rect.bottom === 0 && rect.width === 0 && rect.height === 0)) {
                rect = editorRef.current.getBoundingClientRect();
            }
            if (!rect) return;

            // Viewport dimensions with mobile visualViewport awareness (e.g. iOS/Android virtual keyboard)
            const vv = window.visualViewport;
            const viewportTop = vv ? vv.offsetTop : 0;
            const viewportHeight = vv ? vv.height : window.innerHeight;
            const viewportBottom = viewportTop + viewportHeight;

            // Top safe margin accounts for sticky/fixed navigation bar (~75px)
            const topSafeZone = viewportTop + 75;
            // Bottom safe margin accounts for floating admin bar (~80px) and virtual keyboard breathing room
            const bottomSafeZone = viewportBottom - 85;

            if (rect.top < topSafeZone) {
                const diff = rect.top - topSafeZone - 20;
                window.scrollBy({ top: diff, behavior: smooth ? 'smooth' : 'instant' });
            } else if (rect.bottom > bottomSafeZone) {
                const diff = rect.bottom - bottomSafeZone + 25;
                window.scrollBy({ top: diff, behavior: smooth ? 'smooth' : 'instant' });
            }
        } catch (e) {
            // Silently ignore measurement errors during rapid DOM mutations
        }
    };

    // Check for font-size: 0 in edit mode
    let effectiveStyle = { ...style };
    if (effectiveStyle.fontSize) {
        const fsStr = String(effectiveStyle.fontSize).trim().toLowerCase();
        if (fsStr === '0' || fsStr === '0px' || fsStr === '0rem' || fsStr === '0em') {
            effectiveStyle.fontSize = '1px'; // Just set it to 1, and do not allow a 0 entry
        }
    }

    useEffect(() => {
        if (!isActive) return;
        const handleClickOutside = (e) => {
            if (!e || !e.target) return;
            // Ignore toolbar elements and native form controls (select/option/color inputs) on iOS Safari
            if (e.target.closest && e.target.closest('[data-draggable-toolbar="true"]')) {
                return;
            }
            const tag = e.target.tagName ? e.target.tagName.toUpperCase() : '';
            if (tag === 'OPTION' || tag === 'OPTGROUP' || tag === 'SELECT' || tag === 'INPUT' || tag === 'BUTTON' || tag === 'LABEL') {
                return;
            }
            if (containerRef.current && containerRef.current.contains(e.target)) {
                return;
            }
            setIsActive(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('pointerdown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('pointerdown', handleClickOutside);
        };
    }, [isActive]);

    // Live real-time selection listener for iOS, Touch, and Desktop
    useEffect(() => {
        if (!isEditing) return;
        const handleSelectionChange = () => {
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0 && editorRef.current) {
                try {
                    const range = sel.getRangeAt(0);
                    const container = range.commonAncestorContainer.nodeType === Node.TEXT_NODE
                        ? range.commonAncestorContainer.parentNode
                        : range.commonAncestorContainer;
                    if (editorRef.current === container || editorRef.current.contains(container)) {
                        savedRangeRef.current = range.cloneRange();
                        savedOffsetRef.current = getSelectionOffsets();
                    }
                } catch (e) {
                    // Ignore range cloning errors if selection changes during DOM updates
                }
            }
        };
        document.addEventListener('selectionchange', handleSelectionChange);
        return () => document.removeEventListener('selectionchange', handleSelectionChange);
    }, [isEditing]);

    // Keep caret / selection in view on mobile virtual keyboard resize and window resize
    useEffect(() => {
        if (!isEditing || !isActive) return;
        const handleViewportChange = () => {
            keepEditingPointInView({ smooth: true });
        };
        const vv = typeof window !== 'undefined' ? window.visualViewport : null;
        if (vv) {
            vv.addEventListener('resize', handleViewportChange);
            vv.addEventListener('scroll', handleViewportChange);
        }
        window.addEventListener('resize', handleViewportChange);
        return () => {
            if (vv) {
                vv.removeEventListener('resize', handleViewportChange);
                vv.removeEventListener('scroll', handleViewportChange);
            }
            window.removeEventListener('resize', handleViewportChange);
        };
    }, [isEditing, isActive]);

    const saveSelection = () => {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0 && editorRef.current) {
            try {
                const range = selection.getRangeAt(0);
                const container = range.commonAncestorContainer.nodeType === Node.TEXT_NODE
                    ? range.commonAncestorContainer.parentNode
                    : range.commonAncestorContainer;
                if (editorRef.current === container || editorRef.current.contains(container)) {
                    savedRangeRef.current = range.cloneRange();
                    savedOffsetRef.current = getSelectionOffsets();
                }
            } catch (e) {
                // Ignore range cloning errors
            }
        }
    };

    const restoreSelection = () => {
        if (editorRef.current && document.activeElement !== editorRef.current) {
            try {
                editorRef.current.focus({ preventScroll: true });
            } catch (e) {
                try { editorRef.current.focus(); } catch (err) {}
            }
        }
        if (savedRangeRef.current && editorRef.current) {
            try {
                const container = savedRangeRef.current.commonAncestorContainer.nodeType === Node.TEXT_NODE
                    ? savedRangeRef.current.commonAncestorContainer.parentNode
                    : savedRangeRef.current.commonAncestorContainer;
                if (editorRef.current === container || editorRef.current.contains(container)) {
                    const selection = window.getSelection();
                    selection.removeAllRanges();
                    selection.addRange(savedRangeRef.current);
                    keepEditingPointInView({ smooth: false });
                    return;
                }
            } catch (e) {
                console.error("restoreSelection range error:", e);
            }
        }
        if (savedOffsetRef.current) {
            restoreSelectionOffsets(savedOffsetRef.current);
            keepEditingPointInView({ smooth: false });
        }
    };

    // Helper for toolbar buttons: prevents focus loss on both touch and mouse events on iOS/desktop
    const makeBtnProps = (actionFn) => {
        let lastTouch = 0;
        return {
            onMouseDown: (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (Date.now() - lastTouch < 500) return;
                actionFn(e);
            },
            onTouchStart: (e) => {
                e.preventDefault();
                e.stopPropagation();
                lastTouch = Date.now();
                actionFn(e);
            }
        };
    };

    // 1. SYNC LOGIC
    useEffect(() => {
        if (isTypingRef.current) {
            isTypingRef.current = false;
            return;
        }
        if (editorRef.current) {
            const hasHtmlContent = typeof value === 'string' && (isRichText || value.includes('<a ') || value.includes('<img ') || value.includes('<p>') || value.includes('<div>'));
            const resolvedValue = hasHtmlContent ? resolveHtmlMediaUrls(value || "") : (value || "");
            if (!isEditing) {
                if (editorRef.current.innerHTML !== resolvedValue) {
                    editorRef.current.innerHTML = resolvedValue;
                }
            } else {
                // Only update if content is different and editor does not have active focus (or toolbar is being interacted with)
                const isDirectlyFocused = editorRef.current === document.activeElement || editorRef.current.contains(document.activeElement);
                const isToolbarActive = isActive && document.activeElement && document.activeElement.closest && document.activeElement.closest('[data-draggable-toolbar="true"]');
                const isFocused = isDirectlyFocused || isToolbarActive;

                if (!isFocused && editorRef.current.innerHTML !== resolvedValue) {
                    const hadSelection = editorRef.current.contains(window.getSelection()?.anchorNode);
                    editorRef.current.innerHTML = resolvedValue;
                    if (hadSelection && savedOffsetRef.current) {
                        restoreSelectionOffsets(savedOffsetRef.current);
                    }
                }
            }
        }
    }, [isEditing, value, isRichText, isActive]);

    // Live DOM style synchronization during edit mode and view mode
    useEffect(() => {
        if (editorRef.current) {
            const s = effectiveStyle || {};
            if (s.fontFamily !== undefined) editorRef.current.style.fontFamily = s.fontFamily || '';
            if (s.fontSize !== undefined) editorRef.current.style.fontSize = s.fontSize || '';
            if (s.lineHeight !== undefined) editorRef.current.style.lineHeight = s.lineHeight || '';
            if (s.color !== undefined) {
                editorRef.current.style.color = s.color || '';
                if (s.color && s.color !== 'transparent') {
                    editorRef.current.querySelectorAll('font[color], span[style*="color"]').forEach(el => {
                        el.style.color = '';
                        el.removeAttribute('color');
                    });
                }
            }
            if (s.textAlign !== undefined || currentStyles?.align !== undefined) {
                const alignVal = s.textAlign || currentStyles?.align || '';
                editorRef.current.style.textAlign = alignVal;
                if (alignVal === 'justify') {
                    editorRef.current.style.width = '100%';
                    editorRef.current.style.display = 'block';
                }
            }
            if (s.textTransform !== undefined || currentStyles?.transform !== undefined || currentStyles?.textTransform !== undefined) {
                editorRef.current.style.textTransform = s.textTransform || currentStyles?.transform || currentStyles?.textTransform || '';
            }
            if (s.fontWeight !== undefined || currentStyles?.fontWeight !== undefined || currentStyles?.weight !== undefined) {
                editorRef.current.style.fontWeight = s.fontWeight || currentStyles?.fontWeight || currentStyles?.weight || '';
            }
            if (s.letterSpacing !== undefined || currentStyles?.letterSpacing !== undefined) {
                editorRef.current.style.letterSpacing = s.letterSpacing || currentStyles?.letterSpacing || '';
            }
            if (s.textShadow !== undefined) editorRef.current.style.textShadow = s.textShadow || '';
            if (s.backgroundImage !== undefined) editorRef.current.style.backgroundImage = s.backgroundImage || '';
            if (s.WebkitBackgroundClip !== undefined) editorRef.current.style.WebkitBackgroundClip = s.WebkitBackgroundClip || '';
            if (s.WebkitTextFillColor !== undefined) editorRef.current.style.WebkitTextFillColor = s.WebkitTextFillColor || '';
            if (s.backgroundClip !== undefined) editorRef.current.style.backgroundClip = s.backgroundClip || '';
            if (s.width !== undefined) editorRef.current.style.width = s.width || '';
            if (s.display !== undefined) editorRef.current.style.display = s.display || '';
            if (s.marginTop !== undefined) editorRef.current.style.marginTop = s.marginTop || '';
            if (s.marginBottom !== undefined) editorRef.current.style.marginBottom = s.marginBottom || '';
            if (s.margin !== undefined) editorRef.current.style.margin = s.margin || '';
            if (s.padding !== undefined) editorRef.current.style.padding = s.padding || '';
        }
    }, [isEditing, style, currentStyles]);

    const exec = (cmd, arg = null) => {
        restoreSelection();
        try {
            document.execCommand(cmd, false, arg);
        } catch (e) {
            console.error(`execCommand ${cmd} failed:`, e);
        }
        saveSelection();
        if (editorRef.current) onChange({ target: { value: cleanAllLinks(editorRef.current.innerHTML) } });
    };

    const applyCustomFontSize = (rawVal, skipRestore = false) => {
        if (!rawVal) return;
        if (!skipRestore) {
            restoreSelection();
        }

        let formattedSize = String(rawVal).trim();
        if (/^\d+(\.\d+)?$/.test(formattedSize)) {
            const num = parseFloat(formattedSize);
            formattedSize = num > 8 ? `${num}px` : `${num}rem`;
        }

        const sel = window.getSelection();
        const hasSelection = sel && sel.rangeCount > 0 && !sel.getRangeAt(0).collapsed && editorRef.current && editorRef.current.contains(sel.getRangeAt(0).commonAncestorContainer);

        if (hasSelection) {
            document.execCommand('fontSize', false, '7');
            if (editorRef.current) {
                const fontEls = editorRef.current.querySelectorAll('font[size="7"]');
                fontEls.forEach(fontEl => {
                    fontEl.removeAttribute('size');
                    fontEl.style.fontSize = formattedSize;
                });
                onChange({ target: { value: cleanAllLinks(editorRef.current.innerHTML) } });
            }
            if (!skipRestore) {
                saveSelection();
            }
        } else if (editorRef.current) {
            editorRef.current.style.fontSize = formattedSize;
            if (onStyleChange) {
                onStyleChange('Size', formattedSize);
            }
            if (onChange) {
                onChange({ target: { value: cleanAllLinks(editorRef.current.innerHTML) } });
            }
            if (!skipRestore) {
                restoreSelection();
            }
        }
    };

    const execBlockFormat = (val) => {
        restoreSelection();
        if (!val) return;
        if (val.startsWith('blockquote')) {
            try {
                document.execCommand('formatBlock', false, 'blockquote');
                const sel = window.getSelection();
                if (sel && sel.rangeCount > 0) {
                    let node = sel.anchorNode;
                    while (node && node !== editorRef.current) {
                        if (node.nodeName === 'BLOCKQUOTE') {
                            node.className = (node.className || '').replace(/\b(quote-\w+|pstyle-\w+|list-\w+)\b/g, '').trim();
                            const variant = val.replace('blockquote-', '');
                            if (variant !== 'blockquote' && variant !== 'classic' && variant !== '') {
                                node.classList.add(`quote-${variant}`);
                            } else {
                                node.classList.add('quote-classic');
                            }
                            break;
                        }
                        node = node.parentNode;
                    }
                }
            } catch (e) {
                console.error("formatBlock quote failed:", e);
            }
        } else if (val.startsWith('ul-') || val === 'ul') {
            try {
                const sel = window.getSelection();
                let inUl = false;
                if (sel && sel.rangeCount > 0) {
                    let node = sel.anchorNode;
                    while (node && node !== editorRef.current) {
                        if (node.nodeName === 'UL') {
                            inUl = true;
                            break;
                        }
                        node = node.parentNode;
                    }
                }
                if (!inUl) {
                    document.execCommand('insertUnorderedList', false, null);
                }
                const variant = val.replace('ul-', '');
                if (sel && sel.rangeCount > 0) {
                    let node = sel.anchorNode;
                    while (node && node !== editorRef.current) {
                        if (node.nodeName === 'UL') {
                            node.className = (node.className || '').replace(/\blist-\w+\b/g, '').trim();
                            if (variant && variant !== 'ul' && variant !== 'disc') {
                                node.classList.add(`list-${variant}`);
                            }
                            break;
                        }
                        node = node.parentNode;
                    }
                }
            } catch (e) {
                console.error("formatBlock ul failed:", e);
            }
        } else if (val.startsWith('ol-') || val === 'ol') {
            try {
                const sel = window.getSelection();
                let inOl = false;
                if (sel && sel.rangeCount > 0) {
                    let node = sel.anchorNode;
                    while (node && node !== editorRef.current) {
                        if (node.nodeName === 'OL') {
                            inOl = true;
                            break;
                        }
                        node = node.parentNode;
                    }
                }
                if (!inOl) {
                    document.execCommand('insertOrderedList', false, null);
                }
                const variant = val.replace('ol-', '');
                if (sel && sel.rangeCount > 0) {
                    let node = sel.anchorNode;
                    while (node && node !== editorRef.current) {
                        if (node.nodeName === 'OL') {
                            node.className = (node.className || '').replace(/\blist-\w+\b/g, '').trim();
                            if (variant && variant !== 'ol' && variant !== 'decimal') {
                                node.classList.add(`list-${variant}`);
                            }
                            break;
                        }
                        node = node.parentNode;
                    }
                }
            } catch (e) {
                console.error("formatBlock ol failed:", e);
            }
        } else if (val.startsWith('p-')) {
            try {
                document.execCommand('formatBlock', false, 'p');
                const sel = window.getSelection();
                if (sel && sel.rangeCount > 0) {
                    let node = sel.anchorNode;
                    while (node && node !== editorRef.current) {
                        if (node.nodeName === 'P' || node.nodeName === 'DIV') {
                            node.className = (node.className || '').replace(/\b(quote-\w+|pstyle-\w+|list-\w+)\b/g, '').trim();
                            const variant = val.replace('p-', '');
                            if (variant && variant !== 'normal') {
                                node.classList.add(`pstyle-${variant}`);
                            }
                            break;
                        }
                        node = node.parentNode;
                    }
                }
            } catch (e) {
                console.error("formatBlock pstyle failed:", e);
            }
        } else {
            try {
                document.execCommand('formatBlock', false, val);
                const sel = window.getSelection();
                if (sel && sel.rangeCount > 0) {
                    let node = sel.anchorNode;
                    while (node && node !== editorRef.current) {
                        if (node.nodeName === (val || '').toUpperCase() || node.nodeName === 'P' || node.nodeName === 'BLOCKQUOTE') {
                            node.className = (node.className || '').replace(/\b(quote-\w+|pstyle-\w+|list-\w+)\b/g, '').trim();
                            break;
                        }
                        node = node.parentNode;
                    }
                }
            } catch (e) {
                console.error("formatBlock failed:", e);
            }
        }
        saveSelection();
        if (editorRef.current) onChange({ target: { value: cleanAllLinks(editorRef.current.innerHTML) } });
    };

    // Deep Clear Formatting function for WebKit / iOS Safari & cross-browser compatibility
    const removeFormatCustom = (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
            e.stopPropagation();
        }
        restoreSelection();

        // 1. Run standard browser removeFormat & unlink commands
        try {
            document.execCommand('removeFormat', false, null);
            document.execCommand('unlink', false, null);
        } catch (err) {
            console.error("Native removeFormat failed:", err);
        }

        // 2. Deep clean WebKit / iOS inline CSS styles and formatting wrappers
        try {
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0 && editorRef.current) {
                const range = sel.getRangeAt(0);
                let container = range.commonAncestorContainer;
                if (container.nodeType === Node.TEXT_NODE) {
                    container = container.parentNode;
                }

                if (editorRef.current.contains(container)) {
                    const nodesToClean = [];

                    if (range.collapsed) {
                        // Cursor position: clean parent formatting elements up to editorRef
                        let curr = container;
                        while (curr && curr !== editorRef.current) {
                            nodesToClean.push(curr);
                            curr = curr.parentNode;
                        }
                    } else {
                        // Range selection: clean elements contained inside or intersecting selection
                        const root = container === editorRef.current ? editorRef.current : container;
                        const allElements = root.querySelectorAll('*');
                        allElements.forEach((el) => {
                            if (sel.containsNode(el, true)) {
                                nodesToClean.push(el);
                            }
                        });
                        if (container !== editorRef.current && !nodesToClean.includes(container)) {
                            nodesToClean.push(container);
                        }
                    }

                    nodesToClean.forEach((el) => {
                        if (!el || !el.parentNode) return;

                        // Strip inline CSS styles and styling attributes (font-weight, font-style, color, font-size, etc.)
                        if (typeof el.removeAttribute === 'function') {
                            el.removeAttribute('style');
                            el.removeAttribute('class');
                            el.removeAttribute('color');
                            el.removeAttribute('face');
                            el.removeAttribute('size');
                        }

                        // Unwrap purely inline formatting wrappers
                        const tagName = el.tagName ? el.tagName.toLowerCase() : '';
                        if (['b', 'i', 'strong', 'em', 'u', 'font', 'span', 'mark', 's', 'strike', 'sub', 'sup'].includes(tagName)) {
                            const parent = el.parentNode;
                            while (el.firstChild) {
                                parent.insertBefore(el.firstChild, el);
                            }
                            parent.removeChild(el);
                        }
                    });
                }
            }
        } catch (err) {
            console.error("removeFormatCustom DOM cleanup error:", err);
        }

        saveSelection();
        if (editorRef.current) {
            onChange({ target: { value: cleanAllLinks(editorRef.current.innerHTML) } });
        }
    };

    const openLinkModal = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        saveSelection();

        const sel = window.getSelection();
        let existingNode = (activeLinkNodeRef.current && editorRef.current?.contains(activeLinkNodeRef.current)) ? activeLinkNodeRef.current : null;
        let existingUrl = existingNode ? (existingNode.getAttribute('href') || '') : '';
        let selectedText = existingNode ? (existingNode.textContent || '') : '';
        let targetImg = null;

        const isImgElement = Tag === 'img' || type === 'image';

        if (isImgElement) {
            targetImg = editorRef.current;
            if (!existingNode && editorRef.current?.parentNode?.nodeName === 'A') {
                existingNode = editorRef.current.parentNode;
                existingUrl = existingNode.getAttribute('href') || '';
            }
            if (!existingUrl && imageLink) {
                existingUrl = imageLink;
            }
        } else if (sel && sel.rangeCount > 0) {
            try {
                selectedText = sel.toString();
            } catch (err) {}
            let node = sel.anchorNode;
            
            // Check if selected element is an image
            if (node && node.nodeType === Node.ELEMENT_NODE && node.nodeName === 'IMG') {
                targetImg = node;
            } else if (node && node.nodeType === Node.ELEMENT_NODE && node.querySelector) {
                targetImg = node.querySelector('img');
            }

            while (node && node !== editorRef.current) {
                if (node.nodeName === 'IMG') {
                    targetImg = node;
                }
                if (node.nodeName === 'A') {
                    existingNode = node;
                    existingUrl = node.getAttribute('href') || '';
                    if (!selectedText) {
                        selectedText = node.textContent || '';
                    }
                    const innerImg = node.querySelector('img');
                    if (innerImg) targetImg = innerImg;
                    break;
                }
                node = node.parentNode;
            }
        }

        selectedImgNodeRef.current = targetImg;
        activeLinkNodeRef.current = existingNode;
        setIsImageLinkMode(Boolean(targetImg || isImgElement));

        savedSelectionInfoRef.current = {
            offsets: getSelectionOffsets(),
            range: (sel && sel.rangeCount > 0) ? sel.getRangeAt(0).cloneRange() : null,
            selectedText: selectedText || '',
            targetImg: targetImg
        };

        setLinkUrl(existingUrl || '');
        setLinkText(targetImg ? (targetImg.alt || '[Image Link]') : (selectedText || ''));
        const isExternal = existingUrl ? (existingUrl.startsWith('http://') || existingUrl.startsWith('https://')) : false;
        const initialNewTab = existingNode ? existingNode.getAttribute('target') === '_blank' : (imageLinkNewTab !== undefined ? Boolean(imageLinkNewTab) : isExternal);
        setLinkNewTab(initialNewTab);
        setIsEditingExistingLink(Boolean(existingNode || existingUrl));
        setShowLinkModal(true);
    };

    const applyLink = (overrideUrl, overrideText, overrideNewTab) => {
        const rawUrl = (overrideUrl !== undefined ? overrideUrl : linkUrl).trim();
        const textToUse = (overrideText !== undefined ? overrideText : linkText).trim() || rawUrl;
        const newTab = overrideNewTab !== undefined ? overrideNewTab : linkNewTab;

        const isImgElement = Tag === 'img' || type === 'image';
        const imgNode = selectedImgNodeRef.current || (isImgElement ? editorRef.current : null);

        if (onImageLinkChange && (isImgElement || imgNode)) {
            onImageLinkChange(rawUrl, newTab);
            setShowLinkModal(false);
            selectedImgNodeRef.current = null;
            setIsImageLinkMode(false);
            return;
        }

        // If URL is empty, handle unlinking
        if (!rawUrl) {
            if (activeLinkNodeRef.current && editorRef.current?.contains(activeLinkNodeRef.current)) {
                if (imgNode && activeLinkNodeRef.current.contains(imgNode)) {
                    // Unwrap image from anchor
                    activeLinkNodeRef.current.parentNode.insertBefore(imgNode, activeLinkNodeRef.current);
                    activeLinkNodeRef.current.parentNode.removeChild(activeLinkNodeRef.current);
                } else {
                    const text = activeLinkNodeRef.current.textContent;
                    const textNode = document.createTextNode(text);
                    activeLinkNodeRef.current.parentNode.replaceChild(textNode, activeLinkNodeRef.current);
                }
            } else if (isImgElement && editorRef.current?.parentNode?.nodeName === 'A') {
                const parentA = editorRef.current.parentNode;
                parentA.parentNode.insertBefore(editorRef.current, parentA);
                parentA.parentNode.removeChild(parentA);
            }
            setShowLinkModal(false);
            selectedImgNodeRef.current = null;
            setIsImageLinkMode(false);
            if (editorRef.current && !isImgElement) {
                onChange({ target: { value: cleanAllLinks(editorRef.current.innerHTML) } });
            }
            return;
        }

        let url = rawUrl;
        const isAnchor = url.startsWith('#');
        const isProtocol = /^(https?:\/\/|mailto:|tel:|javascript:)/i.test(url);
        const isSlashRelative = url.startsWith('/');

        if (!isAnchor && !isProtocol && !isSlashRelative) {
            if (url.includes('.') && !url.startsWith('.')) {
                url = 'https://' + url;
            } else {
                url = '/' + url;
            }
        }

        const isExternal = url.startsWith('http://') || url.startsWith('https://');
        const targetAttr = newTab ? ' target="_blank" rel="noopener noreferrer"' : '';

        // CASE 1: Linking an Image
        if (imgNode) {
            if (activeLinkNodeRef.current && editorRef.current?.contains(activeLinkNodeRef.current)) {
                // Update existing wrapping link
                activeLinkNodeRef.current.setAttribute('href', url);
                if (newTab) {
                    activeLinkNodeRef.current.setAttribute('target', '_blank');
                    activeLinkNodeRef.current.setAttribute('rel', 'noopener noreferrer');
                } else {
                    activeLinkNodeRef.current.removeAttribute('target');
                    activeLinkNodeRef.current.removeAttribute('rel');
                }
            } else if (imgNode.parentNode?.nodeName === 'A') {
                imgNode.parentNode.setAttribute('href', url);
                if (newTab) {
                    imgNode.parentNode.setAttribute('target', '_blank');
                    imgNode.parentNode.setAttribute('rel', 'noopener noreferrer');
                } else {
                    imgNode.parentNode.removeAttribute('target');
                    imgNode.parentNode.removeAttribute('rel');
                }
            } else if (imgNode.parentNode) {
                // Wrap the image in a new anchor tag
                const anchor = document.createElement('a');
                anchor.setAttribute('href', url);
                if (newTab) {
                    anchor.setAttribute('target', '_blank');
                    anchor.setAttribute('rel', 'noopener noreferrer');
                }
                imgNode.parentNode.insertBefore(anchor, imgNode);
                anchor.appendChild(imgNode);
            }
        }
        // CASE 2: Existing Text Link Node
        else if (activeLinkNodeRef.current && editorRef.current?.contains(activeLinkNodeRef.current)) {
            activeLinkNodeRef.current.setAttribute('href', url);
            if (newTab) {
                activeLinkNodeRef.current.setAttribute('target', '_blank');
                activeLinkNodeRef.current.setAttribute('rel', 'noopener noreferrer');
            } else {
                activeLinkNodeRef.current.removeAttribute('target');
                activeLinkNodeRef.current.removeAttribute('rel');
            }
            if (textToUse && activeLinkNodeRef.current.textContent !== textToUse) {
                activeLinkNodeRef.current.textContent = textToUse;
            }
        } 
        // CASE 3: New Selection / Range
        else {
            restoreSelection();
            const selInfo = savedSelectionInfoRef.current;
            if (selInfo?.offsets) {
                restoreSelectionOffsets(selInfo.offsets);
            } else if (selInfo?.range) {
                try {
                    const sel = window.getSelection();
                    sel.removeAllRanges();
                    sel.addRange(selInfo.range);
                } catch (e) {}
            }

            const sel = window.getSelection();
            const hasTextSelection = sel && sel.rangeCount > 0 && !sel.getRangeAt(0).collapsed;

            if (hasTextSelection) {
                try {
                    document.execCommand('createLink', false, url);
                    if (editorRef.current) {
                        const links = editorRef.current.querySelectorAll('a');
                        links.forEach(l => {
                            if (l.getAttribute('href') === url) {
                                if (newTab) {
                                    l.setAttribute('target', '_blank');
                                    l.setAttribute('rel', 'noopener noreferrer');
                                } else {
                                    l.removeAttribute('target');
                                    l.removeAttribute('rel');
                                }
                            }
                        });
                    }
                } catch (e) {
                    const linkHtml = `<a href="${url}"${targetAttr}>${textToUse}</a>`;
                    document.execCommand('insertHTML', false, linkHtml);
                }
            } else {
                const linkHtml = `<a href="${url}"${targetAttr}>${textToUse}</a>`;
                try {
                    document.execCommand('insertHTML', false, linkHtml);
                } catch (e) {
                    if (savedRangeRef.current) {
                        try {
                            const range = savedRangeRef.current;
                            range.deleteContents();
                            const el = document.createElement('div');
                            el.innerHTML = linkHtml;
                            const frag = document.createDocumentFragment();
                            let node;
                            while ((node = el.firstChild)) {
                                frag.appendChild(node);
                            }
                            range.insertNode(frag);
                        } catch (err2) {
                            if (editorRef.current) editorRef.current.innerHTML += linkHtml;
                        }
                    } else if (editorRef.current) {
                        editorRef.current.innerHTML += linkHtml;
                    }
                }
            }
        }

        setShowLinkModal(false);
        selectedImgNodeRef.current = null;
        setIsImageLinkMode(false);
        if (editorRef.current && !isImgElement) {
            saveSelection();
            onChange({ target: { value: cleanAllLinks(editorRef.current.innerHTML) } });
        }
    };

    const removeLink = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        restoreSelection();
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
            let node = sel.anchorNode;
            while (node && node !== editorRef.current) {
                if (node.nodeName === 'A') {
                    const innerImg = node.querySelector('img');
                    if (innerImg) {
                        node.parentNode.insertBefore(innerImg, node);
                        node.parentNode.removeChild(node);
                    } else {
                        const text = node.textContent;
                        const textNode = document.createTextNode(text);
                        node.parentNode.replaceChild(textNode, node);
                    }
                    break;
                }
                node = node.parentNode;
            }
        }
        try {
            document.execCommand('unlink', false, null);
        } catch (err) {
            console.error("unlink failed:", err);
        }
        saveSelection();
        if (editorRef.current) {
            onChange({ target: { value: cleanAllLinks(editorRef.current.innerHTML) } });
        }
    };

    const renderLinkModal = () => {
        if (!showLinkModal) return null;
        const modalContent = (
            <div
                data-draggable-toolbar="true"
                style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 9999999,
                    background: '#ffffff',
                    border: '1px solid #cbd5e1',
                    borderRadius: '12px',
                    padding: '16px',
                    boxShadow: '0 20px 45px rgba(0,0,0,0.4)',
                    width: '340px',
                    maxWidth: '92vw',
                    boxSizing: 'border-box',
                    color: '#1e293b',
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                }}
                onClick={e => e.stopPropagation()}
                onMouseDown={e => e.stopPropagation()}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                    <div style={{ fontWeight: '700', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', color: '#1e293b' }}>
                        <LuLink size={14} />
                        <span>{isImageLinkMode ? (isEditingExistingLink ? 'Edit Image Link' : 'Add Link to Image') : (isEditingExistingLink ? 'Edit Link' : 'Add Link to Text')}</span>
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            setShowLinkModal(false);
                            selectedImgNodeRef.current = null;
                            setIsImageLinkMode(false);
                        }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: '0 4px', display: 'inline-flex', alignItems: 'center' }}
                    >
                        <LuX size={15} />
                    </button>
                </div>

                {/* Link URL Input */}
                <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px' }}>
                        Target URL or Page Path
                    </label>
                    <input
                        type="text"
                        autoFocus
                        placeholder="e.g. /shop or https://example.com"
                        value={linkUrl}
                        onChange={e => {
                            const val = e.target.value;
                            setLinkUrl(val);
                            if (val.startsWith('/') || val.startsWith('#')) {
                                setLinkNewTab(false);
                            } else if (val.startsWith('http')) {
                                setLinkNewTab(true);
                            }
                        }}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                applyLink();
                            } else if (e.key === 'Escape') {
                                e.preventDefault();
                                setShowLinkModal(false);
                            }
                        }}
                        style={{ width: '100%', height: '32px', fontSize: '12px', padding: '0 8px', border: '1px solid #3b82f6', borderRadius: '6px', boxSizing: 'border-box', outline: 'none' }}
                    />
                </div>

                {/* Quick Chips */}
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '10px' }}>
                    {['/', '/shop', '/about', '/contact', '/privacy', '/terms', '/cart', '#footer'].map(p => (
                        <button
                            key={p}
                            type="button"
                            onClick={() => {
                                setLinkUrl(p);
                                setLinkNewTab(false);
                            }}
                            style={{
                                background: linkUrl === p ? '#3b82f6' : '#f1f5f9',
                                color: linkUrl === p ? '#ffffff' : '#334155',
                                border: '1px solid #cbd5e1',
                                borderRadius: '4px',
                                padding: '2px 6px',
                                fontSize: '10px',
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            {p}
                        </button>
                    ))}
                </div>

                {/* Link Text Input (Hidden/Disabled when linking an image) */}
                {!isImageLinkMode && (
                    <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px' }}>
                            Display Text
                        </label>
                        <input
                            type="text"
                            placeholder="Text to display"
                            value={linkText}
                            onChange={e => setLinkText(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    applyLink();
                                } else if (e.key === 'Escape') {
                                    e.preventDefault();
                                    setShowLinkModal(false);
                                }
                            }}
                            style={{ width: '100%', height: '32px', fontSize: '12px', padding: '0 8px', border: '1px solid #cbd5e1', borderRadius: '6px', boxSizing: 'border-box', outline: 'none' }}
                        />
                    </div>
                )}

                {/* Open in New Tab Checkbox */}
                <div style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#334155', cursor: 'pointer', userSelect: 'none' }}>
                        <input
                            type="checkbox"
                            checked={linkNewTab}
                            onChange={e => setLinkNewTab(e.target.checked)}
                            style={{ cursor: 'pointer' }}
                        />
                        <span>Open in new tab (<code>target="_blank"</code>)</span>
                    </label>
                </div>

                {/* Buttons */}
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center' }}>
                    {isEditingExistingLink && (
                        <button
                            type="button"
                            onClick={() => {
                                if (activeLinkNodeRef.current && editorRef.current?.contains(activeLinkNodeRef.current)) {
                                    if (selectedImgNodeRef.current && activeLinkNodeRef.current.contains(selectedImgNodeRef.current)) {
                                        activeLinkNodeRef.current.parentNode.insertBefore(selectedImgNodeRef.current, activeLinkNodeRef.current);
                                        activeLinkNodeRef.current.parentNode.removeChild(activeLinkNodeRef.current);
                                    } else {
                                        const text = activeLinkNodeRef.current.textContent;
                                        const textNode = document.createTextNode(text);
                                        activeLinkNodeRef.current.parentNode.replaceChild(textNode, activeLinkNodeRef.current);
                                    }
                                } else if (Tag === 'img' || type === 'image') {
                                    if (onImageLinkChange) {
                                        onImageLinkChange('', false);
                                    }
                                    if (editorRef.current?.parentNode?.nodeName === 'A') {
                                        const parentA = editorRef.current.parentNode;
                                        parentA.parentNode.insertBefore(editorRef.current, parentA);
                                        parentA.parentNode.removeChild(parentA);
                                    }
                                }
                                setShowLinkModal(false);
                                selectedImgNodeRef.current = null;
                                setIsImageLinkMode(false);
                                if (editorRef.current && Tag !== 'img' && type !== 'image') {
                                    onChange({ target: { value: cleanAllLinks(editorRef.current.innerHTML) } });
                                }
                            }}
                            style={{
                                background: '#fee2e2',
                                color: '#ef4444',
                                border: '1px solid #fca5a5',
                                borderRadius: '6px',
                                padding: '6px 10px',
                                fontSize: '11px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                marginRight: 'auto',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            <LuUnlink size={13} /> Unlink
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => {
                            setShowLinkModal(false);
                            selectedImgNodeRef.current = null;
                            setIsImageLinkMode(false);
                        }}
                        style={{
                            background: '#f1f5f9',
                            color: '#475569',
                            border: '1px solid #cbd5e1',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: 'pointer'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => applyLink()}
                        style={{
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '6px 14px',
                            fontSize: '11px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            boxShadow: '0 2px 6px rgba(59,130,246,0.3)'
                        }}
                    >
                        ✓ {isEditingExistingLink ? 'Update Link' : 'Insert Link'}
                    </button>
                </div>
            </div>
        );
        if (typeof document !== 'undefined') {
            return createPortal(modalContent, document.body);
        }
        return modalContent;
    };

    const preventFocusLoss = (e) => e.preventDefault();

    // --- VIDEO INSPECTOR & HANDLERS ---
    const inspectVideoNode = (node) => {
        if (!node || !editorRef.current?.contains(node)) {
            setSelectedVideoInfo(null);
            activeVideoNodeRef.current = null;
            return;
        }
        const videoEl = node.nodeName === 'VIDEO' ? node : node.querySelector('video');
        const iframeEl = node.nodeName === 'IFRAME' ? node : node.querySelector('iframe');
        if (!videoEl && !iframeEl) {
            setSelectedVideoInfo(null);
            activeVideoNodeRef.current = null;
            return;
        }
        activeVideoNodeRef.current = node;
        const isIframe = Boolean(iframeEl);
        const isLoop = isIframe
            ? Boolean((iframeEl.getAttribute('src') || '').includes('loop=1') || node.getAttribute('data-video-loop') === 'true')
            : Boolean(videoEl.hasAttribute('loop') || videoEl.loop || node.getAttribute('data-video-loop') === 'true');
        const hasControls = isIframe ? true : Boolean(videoEl.hasAttribute('controls') || videoEl.controls);
        const isAutoplay = isIframe ? Boolean((iframeEl.getAttribute('src') || '').includes('autoplay=1')) : Boolean(videoEl.hasAttribute('autoplay') || videoEl.autoplay);

        setSelectedVideoInfo({
            node,
            isIframe,
            isLoop,
            hasControls,
            isAutoplay,
            src: isIframe ? iframeEl.getAttribute('src') : videoEl.getAttribute('src')
        });
    };

    const handleEditorClick = (e) => {
        setIsActive(true);
        if (!e || !e.target) return;
        const target = e.target;
        const anchor = target.closest ? target.closest('a') : null;
        if (anchor && isEditing) {
            e.preventDefault();
            if (editorRef.current?.contains(anchor)) {
                activeLinkNodeRef.current = anchor;
            }
        }
        if (target.nodeName === 'VIDEO' || target.closest('video') || target.closest('.omnivaso-video-wrapper') || target.nodeName === 'IFRAME' || target.closest('iframe')) {
            const vNode = target.nodeName === 'VIDEO' || target.nodeName === 'IFRAME' ? target : (target.closest('.omnivaso-video-wrapper') || target.closest('video') || target.closest('iframe'));
            inspectVideoNode(vNode);
        } else {
            setSelectedVideoInfo(null);
        }
    };

    const toggleVideoLoop = (node) => {
        if (!node || !editorRef.current?.contains(node)) return;
        const videoEl = node.nodeName === 'VIDEO' ? node : node.querySelector('video');
        const iframeEl = node.nodeName === 'IFRAME' ? node : node.querySelector('iframe');

        if (videoEl) {
            const isNowLoop = !(videoEl.hasAttribute('loop') || videoEl.loop);
            if (isNowLoop) {
                videoEl.setAttribute('loop', '');
                videoEl.loop = true;
            } else {
                videoEl.removeAttribute('loop');
                videoEl.loop = false;
            }
            node.setAttribute('data-video-loop', isNowLoop ? 'true' : 'false');
        } else if (iframeEl) {
            let src = iframeEl.getAttribute('src') || '';
            const isNowLoop = !src.includes('loop=1');
            if (src.includes('youtube.com/embed/')) {
                const idMatch = src.match(/\/embed\/([\w-]{11})/);
                const id = idMatch ? idMatch[1] : '';
                if (isNowLoop) {
                    if (!src.includes('loop=1')) src += (src.includes('?') ? '&' : '?') + `loop=1&playlist=${id}`;
                } else {
                    src = src.replace(/[?&]loop=1(&playlist=[\w-]+)?/g, '').replace(/&$/, '');
                }
            } else if (src.includes('player.vimeo.com/video/')) {
                if (isNowLoop) {
                    if (!src.includes('loop=1')) src += (src.includes('?') ? '&' : '?') + 'loop=1';
                } else {
                    src = src.replace(/[?&]loop=1/g, '').replace(/&$/, '');
                }
            }
            iframeEl.setAttribute('src', src);
            node.setAttribute('data-video-loop', isNowLoop ? 'true' : 'false');
        }

        if (editorRef.current) {
            onChange({ target: { value: cleanAllLinks(editorRef.current.innerHTML) } });
        }
        inspectVideoNode(node);
    };

    const toggleVideoControls = (node) => {
        if (!node || !editorRef.current?.contains(node)) return;
        const videoEl = node.nodeName === 'VIDEO' ? node : node.querySelector('video');
        if (videoEl) {
            const hasC = videoEl.hasAttribute('controls') || videoEl.controls;
            if (hasC) {
                videoEl.removeAttribute('controls');
                videoEl.controls = false;
            } else {
                videoEl.setAttribute('controls', '');
                videoEl.controls = true;
            }
            if (editorRef.current) {
                onChange({ target: { value: cleanAllLinks(editorRef.current.innerHTML) } });
            }
            inspectVideoNode(node);
        }
    };

    const toggleVideoAutoplay = (node) => {
        if (!node || !editorRef.current?.contains(node)) return;
        const videoEl = node.nodeName === 'VIDEO' ? node : node.querySelector('video');
        if (videoEl) {
            const isAuto = videoEl.hasAttribute('autoplay') || videoEl.autoplay;
            if (isAuto) {
                videoEl.removeAttribute('autoplay');
                videoEl.removeAttribute('muted');
                videoEl.autoplay = false;
            } else {
                videoEl.setAttribute('autoplay', '');
                videoEl.setAttribute('muted', '');
                videoEl.autoplay = true;
                videoEl.muted = true;
            }
            if (editorRef.current) {
                onChange({ target: { value: cleanAllLinks(editorRef.current.innerHTML) } });
            }
            inspectVideoNode(node);
        }
    };

    const deleteVideoNode = (node) => {
        if (!node || !editorRef.current?.contains(node)) return;
        node.parentNode?.removeChild(node);
        setSelectedVideoInfo(null);
        activeVideoNodeRef.current = null;
        if (editorRef.current) {
            onChange({ target: { value: cleanAllLinks(editorRef.current.innerHTML) } });
        }
    };

    const openVideoModal = (e, existingNode = null) => {
        if (e && e.preventDefault) e.preventDefault();
        saveSelection();

        let targetNode = existingNode || activeVideoNodeRef.current;
        if (!targetNode && editorRef.current) {
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0) {
                let n = sel.anchorNode;
                while (n && n !== editorRef.current) {
                    if (n.nodeName === 'VIDEO' || n.nodeName === 'IFRAME' || (n.classList && n.classList.contains('omnivaso-video-wrapper'))) {
                        targetNode = n;
                        break;
                    }
                    n = n.parentNode;
                }
            }
        }

        if (targetNode) {
            activeVideoNodeRef.current = targetNode;
            const videoEl = targetNode.nodeName === 'VIDEO' ? targetNode : targetNode.querySelector('video');
            const iframeEl = targetNode.nodeName === 'IFRAME' ? targetNode : targetNode.querySelector('iframe');
            
            if (videoEl) {
                setVideoInputUrl(videoEl.getAttribute('src') || '');
                setVideoLoopSetting(videoEl.hasAttribute('loop') || videoEl.loop);
                setVideoAutoplaySetting(videoEl.hasAttribute('autoplay') || videoEl.autoplay);
                setVideoControlsSetting(videoEl.hasAttribute('controls') || videoEl.controls);
                setIsEditingExistingVideo(true);
            } else if (iframeEl) {
                const src = iframeEl.getAttribute('src') || '';
                setVideoInputUrl(src);
                setVideoLoopSetting(src.includes('loop=1'));
                setVideoAutoplaySetting(src.includes('autoplay=1'));
                setVideoControlsSetting(true);
                setIsEditingExistingVideo(true);
            } else {
                setVideoInputUrl('');
                setVideoLoopSetting(true);
                setVideoAutoplaySetting(false);
                setVideoControlsSetting(true);
                setIsEditingExistingVideo(false);
            }
        } else {
            activeVideoNodeRef.current = null;
            setVideoInputUrl('');
            setVideoLoopSetting(true);
            setVideoAutoplaySetting(false);
            setVideoControlsSetting(true);
            setIsEditingExistingVideo(false);
        }

        setShowVideoModal(true);
    };

    const applyVideoModal = () => {
        const rawUrl = videoInputUrl.trim();
        if (!rawUrl) {
            setShowVideoModal(false);
            return;
        }

        const targetNode = activeVideoNodeRef.current;
        const isEditing = Boolean(targetNode && editorRef.current?.contains(targetNode));

        const embedUrl = getEmbedUrl(rawUrl, {
            loop: videoLoopSetting,
            autoPlay: videoAutoplaySetting,
            mute: videoAutoplaySetting
        });

        if (isEditing) {
            const videoEl = targetNode.nodeName === 'VIDEO' ? targetNode : targetNode.querySelector('video');
            const iframeEl = targetNode.nodeName === 'IFRAME' ? targetNode : targetNode.querySelector('iframe');

            if (embedUrl) {
                if (iframeEl) {
                    iframeEl.src = embedUrl;
                    targetNode.setAttribute('data-video-loop', videoLoopSetting ? 'true' : 'false');
                } else if (videoEl) {
                    const wrapper = document.createElement('div');
                    wrapper.className = 'omnivaso-video-wrapper';
                    wrapper.setAttribute('data-video-loop', videoLoopSetting ? 'true' : 'false');
                    wrapper.style.cssText = 'position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%; margin: 15px 0; border-radius: 8px; background: #000;';
                    wrapper.innerHTML = `<iframe src="${embedUrl}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"></iframe>`;
                    targetNode.parentNode.replaceChild(wrapper, targetNode);
                }
            } else {
                const resolvedUrl = resolveMediaUrl(rawUrl);
                if (videoEl) {
                    videoEl.src = resolvedUrl;
                    if (videoLoopSetting) { videoEl.setAttribute('loop', ''); videoEl.loop = true; } else { videoEl.removeAttribute('loop'); videoEl.loop = false; }
                    if (videoAutoplaySetting) { videoEl.setAttribute('autoplay', ''); videoEl.setAttribute('muted', ''); videoEl.autoplay = true; videoEl.muted = true; } else { videoEl.removeAttribute('autoplay'); videoEl.removeAttribute('muted'); videoEl.autoplay = false; }
                    if (videoControlsSetting) { videoEl.setAttribute('controls', ''); videoEl.controls = true; } else { videoEl.removeAttribute('controls'); videoEl.controls = false; }
                    targetNode.setAttribute('data-video-loop', videoLoopSetting ? 'true' : 'false');
                } else if (iframeEl) {
                    const wrapper = document.createElement('div');
                    wrapper.className = 'omnivaso-video-wrapper';
                    wrapper.setAttribute('data-video-loop', videoLoopSetting ? 'true' : 'false');
                    wrapper.style.cssText = 'max-width: 100%; margin: 15px 0; border-radius: 8px; overflow: hidden; background: #000;';
                    wrapper.innerHTML = `<video src="${resolvedUrl}" ${videoControlsSetting ? 'controls' : ''} ${videoLoopSetting ? 'loop' : ''} ${videoAutoplaySetting ? 'autoplay muted' : ''} playsinline style="width: 100%; max-height: 480px; display: block; border-radius: 8px;"></video>`;
                    targetNode.parentNode.replaceChild(wrapper, targetNode);
                }
            }
        } else {
            if (embedUrl) {
                const html = `
                    <div class="omnivaso-video-wrapper" data-video-loop="${videoLoopSetting ? 'true' : 'false'}" style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%; margin: 15px 0; border-radius: 8px; background: #000;">
                        <iframe src="${embedUrl}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"></iframe>
                    </div><p><br/></p>
                `;
                exec('insertHTML', html);
            } else {
                const resolvedUrl = resolveMediaUrl(rawUrl);
                const html = `
                    <div class="omnivaso-video-wrapper" data-video-loop="${videoLoopSetting ? 'true' : 'false'}" style="max-width: 100%; margin: 15px 0; border-radius: 8px; overflow: hidden; background: #000;">
                        <video src="${resolvedUrl}" ${videoControlsSetting ? 'controls' : ''} ${videoLoopSetting ? 'loop' : ''} ${videoAutoplaySetting ? 'autoplay muted' : ''} playsinline style="width: 100%; max-height: 480px; display: block; border-radius: 8px;"></video>
                    </div><p><br/></p>
                `;
                exec('insertHTML', html);
            }
        }

        setShowVideoModal(false);
        activeVideoNodeRef.current = null;
        setSelectedVideoInfo(null);
        if (editorRef.current) {
            saveSelection();
            onChange({ target: { value: cleanAllLinks(editorRef.current.innerHTML) } });
        }
    };

    const renderFloatingVideoToolbar = () => {
        if (!selectedVideoInfo || !selectedVideoInfo.node || !editorRef.current?.contains(selectedVideoInfo.node)) return null;
        return (
            <div
                data-draggable-toolbar="true"
                style={{
                    position: 'sticky',
                    top: '8px',
                    margin: '4px auto 8px',
                    zIndex: 99999,
                    background: '#0f172a',
                    color: '#ffffff',
                    padding: '6px 10px',
                    borderRadius: '8px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    fontSize: '11px',
                    fontWeight: '700',
                    border: '1px solid rgba(255,255,255,0.2)',
                    maxWidth: '90%',
                    width: 'fit-content'
                }}
                onClick={e => e.stopPropagation()}
                onMouseDown={e => e.stopPropagation()}
            >
                <button
                    type="button"
                    onClick={() => toggleVideoLoop(selectedVideoInfo.node)}
                    title={selectedVideoInfo.isLoop ? 'Disable video looping' : 'Enable video looping'}
                    style={{
                        background: selectedVideoInfo.isLoop ? '#2563eb' : 'rgba(255,255,255,0.1)',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '11px',
                        fontWeight: '700'
                    }}
                >
                    🔁 Loop: {selectedVideoInfo.isLoop ? 'ON' : 'OFF'}
                </button>

                {!selectedVideoInfo.isIframe && (
                    <>
                        <button
                            type="button"
                            onClick={() => toggleVideoControls(selectedVideoInfo.node)}
                            title="Toggle controls"
                            style={{
                                background: selectedVideoInfo.hasControls ? '#2563eb' : 'rgba(255,255,255,0.1)',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                cursor: 'pointer',
                                fontSize: '11px'
                            }}
                        >
                            🎛 Controls: {selectedVideoInfo.hasControls ? 'ON' : 'OFF'}
                        </button>
                        <button
                            type="button"
                            onClick={() => toggleVideoAutoplay(selectedVideoInfo.node)}
                            title="Toggle autoplay"
                            style={{
                                background: selectedVideoInfo.isAutoplay ? '#2563eb' : 'rgba(255,255,255,0.1)',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                cursor: 'pointer',
                                fontSize: '11px'
                            }}
                        >
                            ▶ Autoplay: {selectedVideoInfo.isAutoplay ? 'ON' : 'OFF'}
                        </button>
                    </>
                )}

                <button
                    type="button"
                    onClick={() => openVideoModal(null, selectedVideoInfo.node)}
                    title="Edit video URL & options"
                    style={{
                        background: 'rgba(255,255,255,0.15)',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}
                >
                    <LuSettings size={12} /> Edit...
                </button>

                <button
                    type="button"
                    onClick={() => deleteVideoNode(selectedVideoInfo.node)}
                    title="Delete video"
                    style={{
                        background: '#ef4444',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <LuTrash2 size={12} />
                </button>

                <button
                    type="button"
                    onClick={() => setSelectedVideoInfo(null)}
                    title="Dismiss"
                    style={{
                        background: 'transparent',
                        color: '#94a3b8',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0 4px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <LuX size={13} />
                </button>
            </div>
        );
    };

    const renderVideoModal = () => {
        if (!showVideoModal) return null;
        const modalContent = (
            <div
                data-draggable-toolbar="true"
                style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 9999999,
                    background: '#ffffff',
                    border: '1px solid #cbd5e1',
                    borderRadius: '12px',
                    padding: '18px',
                    boxShadow: '0 20px 45px rgba(0,0,0,0.4)',
                    width: '380px',
                    maxWidth: '92vw',
                    boxSizing: 'border-box',
                    color: '#1e293b',
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                }}
                onClick={e => e.stopPropagation()}
                onMouseDown={e => e.stopPropagation()}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                    <div style={{ fontWeight: '800', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px', color: '#0f172a' }}>
                        <LuVideo size={14} />
                        <span>{isEditingExistingVideo ? 'Configure Video & Looping' : 'Insert Video'}</span>
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            setShowVideoModal(false);
                            activeVideoNodeRef.current = null;
                        }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: '0 4px', display: 'inline-flex', alignItems: 'center' }}
                    >
                        <LuX size={15} />
                    </button>
                </div>

                {/* Video URL Input */}
                <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px' }}>
                        Video URL (YouTube, YouTube Shorts, Vimeo, or MP4/WebM)
                    </label>
                    <input
                        type="text"
                        autoFocus
                        placeholder="https://www.youtube.com/watch?v=... or .mp4 URL"
                        value={videoInputUrl}
                        onChange={e => setVideoInputUrl(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                applyVideoModal();
                            } else if (e.key === 'Escape') {
                                e.preventDefault();
                                setShowVideoModal(false);
                            }
                        }}
                        style={{ width: '100%', height: '34px', fontSize: '12px', padding: '0 8px', border: '1px solid #3b82f6', borderRadius: '6px', boxSizing: 'border-box', outline: 'none' }}
                    />
                </div>

                {/* Looping & Playback Controls Box */}
                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={videoLoopSetting}
                            onChange={e => setVideoLoopSetting(e.target.checked)}
                            style={{ marginTop: '2px', cursor: 'pointer' }}
                        />
                        <div>
                            <span style={{ fontSize: '12px', fontWeight: '700', color: '#0f172a', display: 'block' }}>
                                🔁 Loop Video (Continuous Replay)
                            </span>
                            <span style={{ fontSize: '10px', color: '#64748b', display: 'block' }}>
                                Automatically restarts when the video reaches the end
                            </span>
                        </div>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={videoAutoplaySetting}
                            onChange={e => setVideoAutoplaySetting(e.target.checked)}
                            style={{ marginTop: '2px', cursor: 'pointer' }}
                        />
                        <div>
                            <span style={{ fontSize: '12px', fontWeight: '700', color: '#0f172a', display: 'block' }}>
                                ▶ Autoplay (Muted)
                            </span>
                            <span style={{ fontSize: '10px', color: '#64748b', display: 'block' }}>
                                Starts playing automatically when page loads
                            </span>
                        </div>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={videoControlsSetting}
                            onChange={e => setVideoControlsSetting(e.target.checked)}
                            style={{ marginTop: '2px', cursor: 'pointer' }}
                        />
                        <div>
                            <span style={{ fontSize: '12px', fontWeight: '700', color: '#0f172a', display: 'block' }}>
                                🎛 Show Player Controls
                            </span>
                            <span style={{ fontSize: '10px', color: '#64748b', display: 'block' }}>
                                Display play, pause, volume, and progress bars
                            </span>
                        </div>
                    </label>
                </div>

                {/* Modal Buttons */}
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center' }}>
                    {isEditingExistingVideo && (
                        <button
                            type="button"
                            onClick={() => {
                                if (activeVideoNodeRef.current && editorRef.current?.contains(activeVideoNodeRef.current)) {
                                    deleteVideoNode(activeVideoNodeRef.current);
                                }
                                setShowVideoModal(false);
                            }}
                            style={{
                                background: '#fee2e2',
                                color: '#ef4444',
                                border: '1px solid #fca5a5',
                                borderRadius: '6px',
                                padding: '6px 10px',
                                fontSize: '11px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                marginRight: 'auto',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            <LuTrash2 size={13} /> Delete
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => {
                            setShowVideoModal(false);
                            activeVideoNodeRef.current = null;
                        }}
                        style={{
                            background: '#f1f5f9',
                            color: '#475569',
                            border: '1px solid #cbd5e1',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: 'pointer'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={applyVideoModal}
                        style={{
                            background: '#2563eb',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '6px 16px',
                            fontSize: '11px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            boxShadow: '0 2px 6px rgba(37,99,235,0.3)'
                        }}
                    >
                        ✓ {isEditingExistingVideo ? 'Update Video' : 'Insert Video'}
                    </button>
                </div>
            </div>
        );
        if (typeof document !== 'undefined') {
            return createPortal(modalContent, document.body);
        }
        return modalContent;
    };

    const insertVideoHtml = (embedUrl, loop = true) => {
        const html = `
            <div class="omnivaso-video-wrapper" data-video-loop="${loop ? 'true' : 'false'}" style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%; margin: 15px 0; border-radius: 8px; background: #000;">
                <iframe src="${embedUrl}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"></iframe>
            </div><p><br/></p>
        `;
        exec('insertHTML', html);
    };

    const addVideo = (e) => {
        openVideoModal(e);
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        restoreSelection(); // restore where we want to insert
        const isVid = (file.type || '').startsWith('video/');

        try {
            const res = await uploadMediaFile(file);
            if (res && res.url) {
                const resolvedUrl = res.isDataUrl ? res.url : resolveMediaUrl(res.url);
                if (isVid) {
                    const html = `
                        <div class="omnivaso-video-wrapper" data-video-loop="true" style="max-width: 100%; margin: 15px 0; border-radius: 8px; overflow: hidden; background: #000;">
                            <video src="${resolvedUrl}" controls loop playsinline style="width: 100%; max-height: 480px; display: block; border-radius: 8px;"></video>
                        </div><p><br/></p>
                    `;
                    exec('insertHTML', html);
                } else {
                    const html = `<img src="${resolvedUrl}" style="max-width: 100%; height: auto; border-radius: 8px; margin: 15px 0;" /><p><br/></p>`;
                    exec('insertHTML', html);
                }
            }
        } catch (err) {
            console.error('Image upload failed in Editable:', err);
        } finally {
            if (e.target) e.target.value = '';
        }
    };

    const handleSelectMediaFromLibrary = (selectedUrl, item) => {
        if (!selectedUrl) return;
        if (type === 'image' || Tag === 'img') {
            if (onChange) onChange({ target: { value: selectedUrl } });
            setShowMediaLibraryModal(false);
            return;
        }
        restoreSelection();
        const resolvedUrl = resolveMediaUrl(selectedUrl);
        const isVid = isVideo(selectedUrl) || item?.type === 'video';

        if (isVid) {
            const html = `
                <div class="omnivaso-video-wrapper" data-video-loop="true" style="max-width: 100%; margin: 15px 0; border-radius: 8px; overflow: hidden; background: #000;">
                    <video src="${resolvedUrl}" controls loop playsinline style="width: 100%; max-height: 480px; display: block; border-radius: 8px;"></video>
                </div><p><br/></p>
            `;
            exec('insertHTML', html);
        } else {
            const html = `<img src="${resolvedUrl}" style="max-width: 100%; height: auto; border-radius: 8px; margin: 15px 0;" /><p><br/></p>`;
            exec('insertHTML', html);
        }
        setShowMediaLibraryModal(false);
    };

    const handlePaste = (e) => {
        const text = e.clipboardData.getData('text/plain');
        const html = e.clipboardData.getData('text/html');
        
        // 1. Check for video embed URLs first (both dashboard and live editors)
        const embedUrl = getEmbedUrl(text, { loop: true });
        if (embedUrl && isRichText) {
            e.preventDefault();
            insertVideoHtml(embedUrl, true);
            return;
        }

        if (isRichText && (isVideo(text) || (typeof text === 'string' && text.trim().match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i)))) {
            e.preventDefault();
            const resolvedUrl = resolveMediaUrl(text.trim());
            const vHtml = `
                <div class="omnivaso-video-wrapper" data-video-loop="true" style="max-width: 100%; margin: 15px 0; border-radius: 8px; overflow: hidden; background: #000;">
                    <video src="${resolvedUrl}" controls loop playsinline style="width: 100%; max-height: 480px; display: block; border-radius: 8px;"></video>
                </div><p><br/></p>
            `;
            exec('insertHTML', vHtml);
            return;
        }

        // 2. Clean up text/html when pasting into any editable text fields to prevent broken styles
        e.preventDefault();
        
        if (isRichText) {
            if (html) {
                // Parse the HTML and sanitize it by stripping style, class, id, and other visual attributes
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                
                const cleanNode = (node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const tagName = node.tagName.toLowerCase();
                        if (['script', 'style', 'iframe', 'object', 'embed', 'link'].includes(tagName)) {
                            node.remove();
                            return;
                        }
                        // Strip attributes that override CSS styles from our visual builder, while preserving quote and paragraph style classes
                        if (node.hasAttribute('class')) {
                            const currentClass = node.getAttribute('class') || '';
                            const preservedClasses = currentClass.split(/\s+/).filter(cls => 
                                cls.startsWith('quote-') || cls.startsWith('pstyle-') || cls.startsWith('list-') || cls === 'lead'
                            ).join(' ');
                            if (preservedClasses) {
                                node.setAttribute('class', preservedClasses);
                            } else {
                                node.removeAttribute('class');
                            }
                        }
                        node.removeAttribute('style');
                        node.removeAttribute('id');
                        
                        // Recurse into children
                        Array.from(node.childNodes).forEach(cleanNode);
                    }
                };
                
                cleanNode(doc.body);
                const cleanHtml = doc.body.innerHTML;
                
                // Insert the sanitized HTML
                exec('insertHTML', cleanHtml);
            } else if (text) {
                exec('insertText', text);
            }
        } else {
            // For plain text editable elements (like h1, h2, h3, or text inputs), only insert plain text
            exec('insertText', text);
        }
    };

    // --- 2. VIEW MODE ---
    if (!isEditing) {
        const effectiveClassName = className || props.className;
        if (type === 'image') {
            const resolvedValue = resolveMediaUrl(value);
            if (isVideo(resolvedValue)) {
                const shouldAutoPlay = props.isHero || props.autoPlay;
                const shouldLoop = props.loop !== undefined ? Boolean(props.loop) : true;
                if (shouldAutoPlay) {
                    return <video className={effectiveClassName} src={resolvedValue} autoPlay loop={shouldLoop} muted playsInline style={style} {...props} />;
                }
                return <GalleryVideoItem src={resolvedValue} className={effectiveClassName} style={style} loop={shouldLoop} />;
            }
            return <Tag className={effectiveClassName} src={resolvedValue} style={style} {...props} />;
        }
        const resolvedHtml = resolveHtmlMediaUrls(value);
        const tagLower = typeof Tag === 'string' ? Tag.toLowerCase() : '';
        const unwrappedHtml = unwrapSingleParagraph(resolvedHtml, tagLower);

        // If the HTML has no text/media (e.g. empty or stray <br>), do not render empty element in view mode
        if (isEmptyHtml(unwrappedHtml) && !props.children) {
            return null;
        }

        const hasBlockTags = typeof unwrappedHtml === 'string' && /<\/?(p|div|h[1-6]|ul|ol|blockquote)\b/i.test(unwrappedHtml);
        const FinalTag = (tagLower === 'p' && hasBlockTags) ? 'div' : Tag;
        return <FinalTag className={effectiveClassName} data-editable={type} dangerouslySetInnerHTML={{ __html: unwrappedHtml }} style={style} {...props} />;
    }

    // --- 3. EDIT MODE ---

    // CASE A: MEDIA INPUT (Image/Video Overlay)
    if (type === 'image') {
        const isVid = isVideo(value);
        const { className, isHero, autoPlay, loop, ...tagProps } = props;
        const shouldAutoPlay = isHero || autoPlay;
        const shouldLoop = loop !== undefined ? Boolean(loop) : true;

        // Use local blob preview if available (set immediately on file selection),
        // otherwise fall back to the saved server URL.
        const displaySrc = localPreviewSrc || resolveMediaUrl(value);
        const displayIsVid = localPreviewSrc
            ? (localPreviewRef.current?.isVideo ?? isVid)
            : isVid;

        // Called by MediaInput immediately when a file is selected (before upload)
        const handlePreview = (blobUrl, isVid) => {
            if (localPreviewRef.current?.url) {
                URL.revokeObjectURL(localPreviewRef.current.url);
            }
            localPreviewRef.current = { url: blobUrl, isVideo: isVid };
            setLocalPreviewSrc(blobUrl);
        };

        // Called by MediaInput after upload completes (or when URL is typed)
        const handleMediaChange = (e) => {
            const newVal = e?.target?.value;
            // Once the server URL is ready, clear the local blob preview
            if (newVal && newVal !== value) {
                if (localPreviewRef.current?.url) {
                    URL.revokeObjectURL(localPreviewRef.current.url);
                    localPreviewRef.current = null;
                }
                setLocalPreviewSrc(null);
            }
            onChange(e);
        };

        // Container needs to mimic the visual dimensions / classes of the original image
        const containerStyle = {
            position: className?.includes('pgallery-img') && style?.height !== 'auto' ? 'absolute' : 'relative',
            top: className?.includes('pgallery-img') && style?.height !== 'auto' ? 0 : 'auto',
            left: className?.includes('pgallery-img') && style?.height !== 'auto' ? 0 : 'auto',
            width: style?.width || '100%',
            height: style?.height || (className?.includes('pgallery-img') ? '100%' : 'auto'),
            display: 'block',
            overflow: 'visible',
            borderRadius: style?.borderRadius || 'none'
        };

        const mediaStyle = {
            width: className?.includes('pgallery-img') ? '100%' : (style?.width || 'auto'),
            height: className?.includes('pgallery-img') ? (style?.height || '100%') : (style?.height || 'auto'),
            maxHeight: style?.maxHeight,
            maxWidth: style?.maxWidth,
            objectFit: style?.objectFit || 'cover',
            // Dim only when no image has been selected yet
            opacity: displaySrc ? 1 : 0.3,
            border: '2px dashed #3b82f6'
        };

        return (
            <div className={className} style={{ ...containerStyle, ...style }}>
                {displayIsVid ? (
                    shouldAutoPlay ? (
                        <video src={displaySrc} autoPlay loop={shouldLoop} muted playsInline style={mediaStyle} {...tagProps} />
                    ) : (
                        <GalleryVideoItem src={displaySrc} className={className} style={mediaStyle} loop={shouldLoop} />
                    )
                ) : (
                    <Tag src={displaySrc} style={mediaStyle} {...tagProps} />
                )}
                <div style={{
                    position: 'absolute', bottom: '8px', left: '50%', transform: 'translateX(-50%)',
                    width: 'calc(100% - 16px)', maxWidth: '320px', zIndex: 110, background: 'white', padding: '6px 8px', borderRadius: '6px',
                    boxShadow: '0 4px 14px rgba(0,0,0,0.25)', border: '1px solid #cbd5e1', boxSizing: 'border-box'
                }} onClick={e => e.stopPropagation()}>
                    <MediaInput size="small" value={value} onChange={handleMediaChange} onPreview={handlePreview} />
                </div>
            </div>
        );
    }
    if (isDashboard && isRichText) {
        return (
            <div ref={containerRef} style={{
                position: 'relative', width: '100%', border: isActive ? '2px solid #3b82f6' : '1px solid #cbd5e1', borderRadius: '4px', background: 'white', margin: '5px 0'
            }}>
                {/* FLOATING DRAGGABLE TOOLBAR */}
                {isActive && (
                    <DraggableToolbar onClose={() => setIsActive(false)}>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', maxWidth: '480px', alignItems: 'center', padding: '2px 0' }}>
                            {/* 1. Block Format (Paragraph Styles, Headers, Lists & Quote Options) */}
                            <select
                                onFocus={saveSelection}
                                onTouchStart={saveSelection}
                                onChange={(e) => { execBlockFormat(e.target.value); e.target.value = ""; }}
                                title="Block & Paragraph Styles"
                                style={{ flexShrink: 0, height: '32px', border: '1px solid #cbd5e1', borderRadius: '6px', background: 'white', fontSize: '0.8rem', padding: '0 6px', cursor: 'pointer', minWidth: '95px' }}
                            >
                                <option value="" disabled selected>Style</option>
                                <optgroup label="Standard Blocks">
                                    <option value="p">Normal (P)</option>
                                    <option value="h1">Heading 1 (H1)</option>
                                    <option value="h2">Heading 2 (H2)</option>
                                    <option value="h3">Heading 3 (H3)</option>
                                    <option value="h4">Heading 4 (H4)</option>
                                </optgroup>
                                <optgroup label="Paragraph Styles">
                                    <option value="p-lead">Lead Intro (Large)</option>
                                    <option value="p-callout">Callout Note (Bordered)</option>
                                    <option value="p-highlight">Highlight Box (Warm Tint)</option>
                                    <option value="p-caption">Caption / Small Italic</option>
                                    <option value="p-bordered">Framed Box (Dashed)</option>
                                    <option value="p-badge">Pill Badge Accent</option>
                                    <option value="p-bullet-disc">• Bullet Paragraph</option>
                                    <option value="p-bullet-check">✓ Checkmark Paragraph</option>
                                    <option value="p-bullet-arrow">➔ Arrow Paragraph</option>
                                    <option value="p-bullet-star">★ Star Paragraph</option>
                                    <option value="p-bullet-card">📦 Card Bullet Paragraph</option>
                                </optgroup>
                                <optgroup label="Bullet & List Styles">
                                    <option value="ul-disc">• Standard Bullet List</option>
                                    <option value="ul-check">✓ Checklist (Green / Accent)</option>
                                    <option value="ul-arrow">➔ Modern Arrow List</option>
                                    <option value="ul-diamond">◆ Diamond Point List</option>
                                    <option value="ul-star">★ Star Feature List</option>
                                    <option value="ul-pill">🏷️ Pill Badge List Items</option>
                                    <option value="ul-card">📦 Boxed Card List Items</option>
                                    <option value="ol-decimal">1. Numbered List (1, 2, 3)</option>
                                    <option value="ol-roman">I. Roman Numerals (I, II, III)</option>
                                    <option value="ol-alpha">A. Alphabetical (A, B, C)</option>
                                </optgroup>
                                <optgroup label="Quote Options">
                                    <option value="blockquote-classic">Quote (1. Classic)</option>
                                    <option value="blockquote-modern">Quote (2. Modern Accent)</option>
                                    <option value="blockquote-centered">Quote (3. Centered Pull)</option>
                                    <option value="blockquote-boxed">Quote (4. Boxed Card)</option>
                                    <option value="blockquote-minimal">Quote (5. Minimalist)</option>
                                    <option value="blockquote-gradient">Quote (6. Gradient Callout)</option>
                                    <option value="blockquote-speech-bubble">Quote (7. Speech Bubble)</option>
                                    <option value="blockquote-glassmorphic">Quote (8. Glassmorphic Glow)</option>
                                    <option value="blockquote-editorial">Quote (9. Editorial Giant)</option>
                                    <option value="blockquote-neon">Quote (10. Neon Cyber)</option>
                                    <option value="blockquote-double-frame">Quote (11. Double Frame)</option>
                                    <option value="blockquote-pill">Quote (12. Pill Left Badge)</option>
                                </optgroup>
                            </select>

                            {/* 2. Font Size Select */}
                            <select
                                onFocus={saveSelection}
                                onTouchStart={saveSelection}
                                onChange={(e) => { applyCustomFontSize(e.target.value); e.target.value = ""; }}
                                title="Font Size (Selected Text or Element)"
                                style={{ flexShrink: 0, height: '32px', border: '1px solid #cbd5e1', borderRadius: '6px', background: 'white', fontSize: '0.8rem', padding: '0 6px', cursor: 'pointer', minWidth: '70px' }}
                            >
                                <option value="" disabled selected>Size</option>
                                <option value="12px">12px</option>
                                <option value="14px">14px</option>
                                <option value="16px">16px (Normal)</option>
                                <option value="18px">18px</option>
                                <option value="20px">20px</option>
                                <option value="24px">24px (Large)</option>
                                <option value="28px">28px</option>
                                <option value="32px">32px (Huge)</option>
                                <option value="36px">36px</option>
                                <option value="48px">48px</option>
                                <option value="64px">64px</option>
                            </select>

                            {/* Overall Element / Body Text Size Input */}
                            {onStyleChange && (
                                <input
                                    onFocus={saveSelection}
                                    onTouchStart={saveSelection}
                                    placeholder="Size"
                                    title="Overall Body Text Size (e.g. 24px, 1.5rem, 18)"
                                    value={currentStyles?.size || ''}
                                    onChange={e => {
                                        const val = e.target.value;
                                        applyCustomFontSize(val, true);
                                        if (onStyleChange) {
                                            onStyleChange('Size', val);
                                        }
                                    }}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            restoreSelection();
                                        }
                                    }}
                                    style={{ flexShrink: 0, width: '60px', height: '32px', fontSize: '0.8rem', padding: '0 6px', border: '1px solid #3b82f6', borderRadius: '6px', background: 'white', boxSizing: 'border-box', outline: 'none' }}
                                />
                            )}

                            <div style={{ flexShrink: 0, width: '1px', background: '#cbd5e1', margin: '0 2px', height: '22px' }}></div>

                            {/* 3. Text Color Picker */}
                            <div style={{ flexShrink: 0, position: 'relative', width: '32px', height: '32px', border: '1px solid #cbd5e1', borderRadius: '6px', background: 'white', overflow: 'hidden', cursor: 'pointer' }} title="Text Color">
                                <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '14px', fontWeight: 'bold', pointerEvents: 'none' }}>A</span>
                                <input
                                    type="color"
                                    onFocus={saveSelection}
                                    onTouchStart={saveSelection}
                                    onChange={(e) => exec('foreColor', e.target.value)}
                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                                />
                            </div>

                            <div style={{ flexShrink: 0, width: '1px', background: '#cbd5e1', margin: '0 2px', height: '22px' }}></div>

                            {/* 4. Basic Formatting */}
                            <button type="button" {...makeBtnProps(() => exec('bold'))} style={{ flexShrink: 0, fontWeight: 'bold', width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px' }}>B</button>
                            <button type="button" {...makeBtnProps(() => exec('italic'))} style={{ flexShrink: 0, fontStyle: 'italic', width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px' }}>I</button>
                            <button type="button" {...makeBtnProps(() => exec('underline'))} style={{ flexShrink: 0, textDecoration: 'underline', width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px' }}>U</button>

                            {/* 5. Bullet & Numbered List Buttons */}
                            <button type="button" {...makeBtnProps(() => exec('insertUnorderedList'))} title="Bullet List (•)" style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FaListUl /></button>
                            <button type="button" {...makeBtnProps(() => exec('insertOrderedList'))} title="Numbered List (1, 2, 3)" style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FaListOl /></button>
                            <button type="button" {...makeBtnProps(() => execBlockFormat('ul-check'))} title="Checklist (✓)" style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981' }}><FaCheckSquare /></button>

                            {/* 6. Clear Formatting */}
                            <button type="button" {...makeBtnProps(removeFormatCustom)} title="Clear Formatting" style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '6px', color: '#ef4444', fontWeight: 'bold', fontSize: '12px' }}>Tx</button>

                            <div style={{ flexShrink: 0, width: '1px', background: '#cbd5e1', margin: '0 2px', height: '22px' }}></div>

                            {/* 7. Alignment & Links */}
                            <button type="button" {...makeBtnProps(() => exec('justifyLeft'))} style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Align Left"><FaAlignLeft /></button>
                            <button type="button" {...makeBtnProps(() => exec('justifyCenter'))} style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Align Center"><FaAlignCenter /></button>
                            <button type="button" {...makeBtnProps(() => exec('justifyRight'))} style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Align Right"><FaAlignRight /></button>
                            <button type="button" {...makeBtnProps(() => exec('justifyFull'))} style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Justify"><FaAlignJustify /></button>

                            <div style={{ flexShrink: 0, width: '1px', background: '#cbd5e1', margin: '0 2px', height: '22px' }}></div>

                            <button type="button" {...makeBtnProps(openLinkModal)} title="Add / Edit Link" style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#334155' }}><LuLink size={15} /></button>
                            <button type="button" {...makeBtnProps(removeLink)} title="Remove Link" style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}><LuUnlink size={15} /></button>
                            <button type="button" {...makeBtnProps(addVideo)} title="Embed Video" style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444' }}><LuVideo size={15} /></button>
                            <button type="button" onMouseDown={saveSelection} onClick={() => { saveSelection(); setShowMediaLibraryModal(true); }} title="Select Image from Uploads Library" style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb' }}><LuImage size={15} /></button>
                            <label onMouseDown={saveSelection} style={{ position: 'relative', overflow: 'hidden', flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981' }} title="Upload Image File">
                                <LuUpload size={15} /><input type="file" accept="image/*" onChange={handleImageUpload} style={IOS_FILE_INPUT_STYLE} />
                            </label>
                        </div>
                    </DraggableToolbar>
                )}

                {renderLinkModal()}
                {renderVideoModal()}
                {renderFloatingVideoToolbar()}
                
                <MediaLibraryModal
                    isOpen={showMediaLibraryModal}
                    onClose={() => setShowMediaLibraryModal(false)}
                    onSelect={handleSelectMediaFromLibrary}
                    title="Insert Media from Uploads"
                />

                {/* EDITOR BOX */}
                <Tag
                    data-editable={type}
                    ref={editorRef}
                    contentEditable={true}
                    suppressContentEditableWarning={true}
                    onFocus={(e) => {
                        setIsActive(true);
                        saveSelection();
                        setTimeout(() => {
                            keepEditingPointInView({ smooth: true });
                        }, 150);
                        const text = e.currentTarget.textContent;
                        if (isPlaceholderText(text)) {
                            setTimeout(() => {
                                if (editorRef.current) {
                                    const range = document.createRange();
                                    range.selectNodeContents(editorRef.current);
                                    const sel = window.getSelection();
                                    sel.removeAllRanges();
                                    sel.addRange(range);
                                    saveSelection();
                                }
                            }, 10);
                        }
                    }}
                    onClickCapture={(e) => {
                        if (isEditing) {
                            const anchor = e.target?.closest ? e.target.closest('a') : null;
                            if (anchor) {
                                e.preventDefault();
                            }
                        }
                    }}
                    onClick={(e) => {
                        setIsActive(true);
                        saveSelection();
                        handleEditorClick(e);
                    }}
                    onTouchStart={() => setIsActive(true)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            setTimeout(() => {
                                saveSelection();
                                keepEditingPointInView({ smooth: true });
                            }, 20);
                        }
                        if (e.ctrlKey || e.metaKey || e.altKey) return;
                        const text = e.currentTarget.textContent;
                        if (isPlaceholderText(text)) {
                            e.currentTarget.innerHTML = '';
                        }
                    }}
                    onInput={(e) => {
                        isTypingRef.current = true;
                        saveSelection();
                        keepEditingPointInView({ smooth: false });
                        onChange({ target: { value: cleanAllLinks(e.currentTarget.innerHTML) } });
                    }}
                    onBlur={(e) => {
                        saveSelection();
                        onChange({ target: { value: cleanAllLinks(e.currentTarget.innerHTML) } });
                    }}
                    onMouseUp={() => {
                        saveSelection();
                        keepEditingPointInView({ smooth: false });
                    }}
                    onKeyUp={(e) => {
                        saveSelection();
                        keepEditingPointInView({ smooth: false });
                    }}
                    onTouchEnd={(e) => {
                        saveSelection();
                        setIsActive(true);
                        setTimeout(() => {
                            keepEditingPointInView({ smooth: true });
                        }, 200);
                    }}
                    onPaste={handlePaste}
                    style={{
                        outline: 'none',
                        minHeight: '120px',
                        padding: '15px',
                        cursor: 'text',
                        whiteSpace: 'pre-wrap',
                        WebkitUserSelect: 'text',
                        userSelect: 'text',
                        ...effectiveStyle
                    }}
                    {...props}
                />
            </div>
        );
    }

    // CASE C: LIVE FRONT-END (Pure Inline WYSIWYG with Floating Toolbar)
    const isBlockOrFull = !effectiveStyle?.display || effectiveStyle?.display === 'block' || effectiveStyle?.display === 'flex' || effectiveStyle?.width === '100%' || ['div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'header', 'section', 'article'].includes(Tag);
    const containerWidth = isBlockOrFull ? (effectiveStyle?.width || '100%') : effectiveStyle?.width;

    return (
        <div 
            ref={containerRef} 
            style={{ 
                position: 'relative', 
                width: containerWidth, 
                maxWidth: effectiveStyle?.maxWidth, 
                marginLeft: effectiveStyle?.marginLeft, 
                marginRight: effectiveStyle?.marginRight, 
                textAlign: effectiveStyle?.textAlign, 
                display: effectiveStyle?.display,
                order: effectiveStyle?.order,
                flex: effectiveStyle?.flex,
                flexGrow: effectiveStyle?.flexGrow,
                flexShrink: effectiveStyle?.flexShrink,
                flexBasis: effectiveStyle?.flexBasis,
                alignSelf: effectiveStyle?.alignSelf,
                minHeight: effectiveStyle?.minHeight,
                margin: effectiveStyle?.margin,
                marginTop: effectiveStyle?.marginTop,
                marginBottom: effectiveStyle?.marginBottom
            }}
        >
            {isActive && isEditing && (isRichText || !onStyleChange) && (
                <DraggableToolbar onClose={() => setIsActive(false)}>
                    <div style={{ display: 'flex', gap: '6px', padding: '4px 2px', alignItems: 'center', flexWrap: 'wrap', maxWidth: '480px', boxSizing: 'border-box' }}>
                        <select onFocus={saveSelection} onTouchStart={saveSelection} onChange={(e) => { execBlockFormat(e.target.value); e.target.value = ""; }} title="Block & Paragraph Styles" style={{ flexShrink: 0, height: '32px', fontSize: '0.8rem', padding: '0 6px', border: '1px solid #cbd5e1', borderRadius: '6px', background: 'white', minWidth: '95px' }}>
                            <option value="" disabled selected>Style</option>
                            <optgroup label="Standard Blocks">
                                <option value="p">Normal (P)</option>
                                <option value="h1">Heading 1 (H1)</option>
                                <option value="h2">Heading 2 (H2)</option>
                                <option value="h3">Heading 3 (H3)</option>
                                <option value="h4">Heading 4 (H4)</option>
                            </optgroup>
                            <optgroup label="Paragraph Styles">
                                <option value="p-lead">Lead Intro (Large)</option>
                                <option value="p-callout">Callout Note (Bordered)</option>
                                <option value="p-highlight">Highlight Box (Warm Tint)</option>
                                <option value="p-caption">Caption / Small Italic</option>
                                <option value="p-bordered">Framed Box (Dashed)</option>
                                <option value="p-badge">Pill Badge Accent</option>
                                <option value="p-bullet-disc">• Bullet Paragraph</option>
                                <option value="p-bullet-check">✓ Checkmark Paragraph</option>
                                <option value="p-bullet-arrow">➔ Arrow Paragraph</option>
                                <option value="p-bullet-star">★ Star Paragraph</option>
                                <option value="p-bullet-card">📦 Card Bullet Paragraph</option>
                            </optgroup>
                            <optgroup label="Bullet & List Styles">
                                <option value="ul-disc">• Standard Bullet List</option>
                                <option value="ul-check">✓ Checklist (Green / Accent)</option>
                                <option value="ul-arrow">➔ Modern Arrow List</option>
                                <option value="ul-diamond">◆ Diamond Point List</option>
                                <option value="ul-star">★ Star Feature List</option>
                                <option value="ul-pill">🏷️ Pill Badge List Items</option>
                                <option value="ul-card">📦 Boxed Card List Items</option>
                                <option value="ol-decimal">1. Numbered List (1, 2, 3)</option>
                                <option value="ol-roman">I. Roman Numerals (I, II, III)</option>
                                <option value="ol-alpha">A. Alphabetical (A, B, C)</option>
                            </optgroup>
                            <optgroup label="Quote Options">
                                <option value="blockquote-classic">Quote (1. Classic)</option>
                                <option value="blockquote-modern">Quote (2. Modern Accent)</option>
                                <option value="blockquote-centered">Quote (3. Centered Pull)</option>
                                <option value="blockquote-boxed">Quote (4. Boxed Card)</option>
                                <option value="blockquote-minimal">Quote (5. Minimalist)</option>
                                <option value="blockquote-gradient">Quote (6. Gradient Callout)</option>
                                <option value="blockquote-speech-bubble">Quote (7. Speech Bubble)</option>
                                <option value="blockquote-glassmorphic">Quote (8. Glassmorphic Glow)</option>
                                <option value="blockquote-editorial">Quote (9. Editorial Giant)</option>
                                <option value="blockquote-neon">Quote (10. Neon Cyber)</option>
                                <option value="blockquote-double-frame">Quote (11. Double Frame)</option>
                                <option value="blockquote-pill">Quote (12. Pill Left Badge)</option>
                            </optgroup>
                        </select>

                        {/* Font Family Dropdown */}
                        {onStyleChange && (
                            <select 
                                onFocus={saveSelection}
                                onTouchStart={saveSelection}
                                value={currentStyles?.fontFamily || ''} 
                                onChange={e => onStyleChange('FontFamily', e.target.value)}
                                title="Font Family"
                                style={{ flexShrink: 0, height: '32px', fontSize: '0.8rem', padding: '0 6px', border: '1px solid #3b82f6', borderRadius: '6px', background: 'white', cursor: 'pointer', boxSizing: 'border-box', outline: 'none', maxWidth: '110px' }}
                            >
                                <option value="">Font</option>
                                {fontOptions.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                            </select>
                        )}

                        {/* Font Size Select */}
                        <select
                            onFocus={saveSelection}
                            onTouchStart={saveSelection}
                            onChange={(e) => { applyCustomFontSize(e.target.value); e.target.value = ""; }}
                            title="Font Size (Selected Text or Element)"
                            style={{ flexShrink: 0, height: '32px', border: '1px solid #cbd5e1', borderRadius: '6px', background: 'white', fontSize: '0.8rem', padding: '0 6px', cursor: 'pointer', minWidth: '70px' }}
                        >
                            <option value="" disabled selected>Size</option>
                            <option value="12px">12px</option>
                            <option value="14px">14px</option>
                            <option value="16px">16px (Normal)</option>
                            <option value="18px">18px</option>
                            <option value="20px">20px</option>
                            <option value="24px">24px (Large)</option>
                            <option value="28px">28px</option>
                            <option value="32px">32px (Huge)</option>
                            <option value="36px">36px</option>
                            <option value="48px">48px</option>
                            <option value="64px">64px</option>
                        </select>

                        {/* Overall Element / Body Text Size Input */}
                        {onStyleChange && (
                            <input
                                onFocus={saveSelection}
                                onTouchStart={saveSelection}
                                placeholder="Size"
                                title="Overall Body Text Size (e.g. 24px, 1.5rem, 18)"
                                value={currentStyles?.size || ''}
                                onChange={e => {
                                    const val = e.target.value;
                                    applyCustomFontSize(val, true);
                                    if (onStyleChange) {
                                        onStyleChange('Size', val);
                                    }
                                }}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        restoreSelection();
                                    }
                                }}
                                style={{ flexShrink: 0, width: '60px', height: '32px', fontSize: '0.8rem', padding: '0 6px', border: '1px solid #3b82f6', borderRadius: '6px', background: 'white', boxSizing: 'border-box', outline: 'none' }}
                            />
                        )}

                        {/* Text Transform Dropdown */}
                        {onStyleChange && (
                            <select 
                                onFocus={saveSelection}
                                onTouchStart={saveSelection}
                                value={currentStyles?.transform || currentStyles?.textTransform || effectiveStyle?.textTransform || 'none'} 
                                onChange={e => {
                                    const val = e.target.value;
                                    if (editorRef.current) {
                                        editorRef.current.style.textTransform = val;
                                    }
                                    if (onStyleChange) {
                                        onStyleChange('Transform', val);
                                        onStyleChange('TextTransform', val);
                                    }
                                }}
                                title="Text Transform (Normal, UPPERCASE, lowercase, Capitalize)"
                                style={{ flexShrink: 0, height: '32px', fontSize: '0.8rem', padding: '0 6px', border: '1px solid #3b82f6', borderRadius: '6px', background: 'white', cursor: 'pointer', boxSizing: 'border-box', outline: 'none' }}
                            >
                                <option value="none">Transform: Normal</option>
                                <option value="uppercase">UPPERCASE</option>
                                <option value="lowercase">lowercase</option>
                                <option value="capitalize">Capitalize</option>
                            </select>
                        )}

                        {/* Text Colour Controls: Swatch + Direct HEX Input */}
                        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <div style={{ position: 'relative', width: '32px', height: '32px', border: '1px solid #cbd5e1', borderRadius: '6px', background: currentStyles?.color || 'white', overflow: 'hidden', cursor: 'pointer' }} title="Text Colour Picker">
                                <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '14px', fontWeight: 'bold', pointerEvents: 'none', color: currentStyles?.color ? '#fff' : '#000' }}>A</span>
                                <input 
                                    type="color" 
                                    onFocus={saveSelection} 
                                    onMouseDown={saveSelection}
                                    onTouchStart={saveSelection} 
                                    value={(currentStyles?.color && currentStyles.color.startsWith('#')) ? currentStyles.color : '#000000'}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        restoreSelection();
                                        const sel = window.getSelection();
                                        const hasSelection = sel && sel.rangeCount > 0 && !sel.getRangeAt(0).collapsed;
                                        if (hasSelection) {
                                            exec('foreColor', val);
                                        }
                                        if (onStyleChange) {
                                            onStyleChange('Color', val);
                                        } else if (!hasSelection) {
                                            exec('foreColor', val);
                                        }
                                    }} 
                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} 
                                />
                            </div>
                            <input
                                type="text"
                                placeholder="#HEX"
                                title="Direct HEX Text Colour Input"
                                value={currentStyles?.color || ''}
                                onFocus={saveSelection}
                                onTouchStart={saveSelection}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (editorRef.current) {
                                        editorRef.current.style.color = val;
                                    }
                                    if (onStyleChange) {
                                        onStyleChange('Color', val);
                                    }
                                }}
                                style={{ flexShrink: 0, width: '70px', height: '32px', fontSize: '0.75rem', padding: '0 6px', border: '1px solid #cbd5e1', borderRadius: '6px', background: 'white', boxSizing: 'border-box', outline: 'none' }}
                            />
                        </div>

                        <div style={{ flexShrink: 0, width: '1px', background: '#cbd5e1', margin: '0 2px', height: '22px' }}></div>

                        <button type="button" {...makeBtnProps(() => exec('bold'))} style={{ flexShrink: 0, fontWeight: 'bold', width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px' }}>B</button>
                        <button type="button" {...makeBtnProps(() => exec('italic'))} style={{ flexShrink: 0, fontStyle: 'italic', width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px' }}>I</button>
                        <button type="button" {...makeBtnProps(() => exec('underline'))} style={{ flexShrink: 0, textDecoration: 'underline', width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px' }}>U</button>

                        {/* Bullet & Numbered List Buttons */}
                        <button type="button" {...makeBtnProps(() => exec('insertUnorderedList'))} title="Bullet List (•)" style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FaListUl size={12} /></button>
                        <button type="button" {...makeBtnProps(() => exec('insertOrderedList'))} title="Numbered List (1, 2, 3)" style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FaListOl size={12} /></button>
                        <button type="button" {...makeBtnProps(() => execBlockFormat('ul-check'))} title="Checklist (✓)" style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981' }}><FaCheckSquare size={12} /></button>

                        {/* CLEAR FORMATTING BUTTON */}
                        <button type="button" {...makeBtnProps(removeFormatCustom)} title="Clear Formatting" style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '6px', color: '#ef4444', fontWeight: 'bold', fontSize: '10px' }}>Tx</button>

                        <div style={{ flexShrink: 0, width: '1px', background: '#cbd5e1', margin: '0 2px', height: '22px' }}></div>

                        <button type="button" {...makeBtnProps(() => { exec('justifyLeft'); if (onStyleChange) onStyleChange('Align', 'left'); })} style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Align Left"><FaAlignLeft size={12} /></button>
                        <button type="button" {...makeBtnProps(() => { exec('justifyCenter'); if (onStyleChange) onStyleChange('Align', 'center'); })} style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Align Center"><FaAlignCenter size={12} /></button>
                        <button type="button" {...makeBtnProps(() => { exec('justifyRight'); if (onStyleChange) onStyleChange('Align', 'right'); })} style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Align Right"><FaAlignRight size={12} /></button>
                        <button type="button" {...makeBtnProps(() => { exec('justifyFull'); if (onStyleChange) onStyleChange('Align', 'justify'); })} style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Justify"><FaAlignJustify size={12} /></button>

                        <div style={{ flexShrink: 0, width: '1px', background: '#cbd5e1', margin: '0 2px', height: '22px' }}></div>
                        <button type="button" {...makeBtnProps(openLinkModal)} title="Add / Edit Link" style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#334155' }}><LuLink size={15} /></button>
                        <button type="button" {...makeBtnProps(removeLink)} title="Remove Link" style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}><LuUnlink size={15} /></button>
                        <button type="button" {...makeBtnProps(addVideo)} title="Embed Video" style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444' }}><LuVideo size={15} /></button>
                        <button type="button" onMouseDown={saveSelection} onClick={() => { saveSelection(); setShowMediaLibraryModal(true); }} title="Select Image from Uploads Library" style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb' }}><LuImage size={15} /></button>
                        <label onMouseDown={saveSelection} style={{ position: 'relative', overflow: 'hidden', flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981' }} title="Upload Image File">
                            <LuUpload size={15} /><input type="file" accept="image/*" onChange={handleImageUpload} style={IOS_FILE_INPUT_STYLE} />
                        </label>
                    </div>
                </DraggableToolbar>
            )}

            {isActive && isEditing && !isRichText && onStyleChange && (
                <DraggableToolbar onClose={() => setIsActive(false)}>
                    <div style={{ display: 'flex', gap: '6px', padding: '6px', alignItems: 'center', background: '#fff', borderLeft: '4px solid #3b82f6', minHeight: '44px', height: 'auto', boxSizing: 'border-box', flexWrap: 'wrap', maxWidth: '480px' }}>
                        <div style={{ fontSize: '0.6rem', fontWeight: 'bold', color: '#3b82f6', marginRight: '2px', lineHeight: '32px' }}>STYLE</div>
                        
                        {/* Text Style / Preset Dropdown */}
                        <select 
                            onFocus={saveSelection}
                            onTouchStart={saveSelection}
                            value={currentStyles?.preset || ''} 
                            onChange={e => onStyleChange('Preset', e.target.value)}
                            style={{ height: '32px', fontSize: '0.8rem', padding: '0 4px', border: '1px solid #3b82f6', borderRadius: '4px', background: 'white', cursor: 'pointer', boxSizing: 'border-box', margin: 0, outline: 'none' }}
                        >
                            <option value="">Text Style</option>
                            <option value="h1">Hero Title (H1)</option>
                            <option value="h2">Section Title (H2)</option>
                            <option value="body">Body Text (P)</option>
                        </select>

                        {/* Font Family Dropdown */}
                        <select 
                            onFocus={saveSelection}
                            onTouchStart={saveSelection}
                            value={currentStyles?.fontFamily || ''} 
                            onChange={e => {
                                const val = e.target.value;
                                if (editorRef.current) editorRef.current.style.fontFamily = val;
                                if (onStyleChange) onStyleChange('FontFamily', val);
                            }}
                            style={{ height: '32px', fontSize: '0.8rem', padding: '0 4px', border: '1px solid #3b82f6', borderRadius: '4px', background: 'white', cursor: 'pointer', boxSizing: 'border-box', margin: 0, outline: 'none', maxWidth: '120px' }}
                        >
                            <option value="">Default Font</option>
                            {fontOptions.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                        </select>

                        {/* Font Weight Dropdown */}
                        <select 
                            onFocus={saveSelection}
                            onTouchStart={saveSelection}
                            value={currentStyles?.fontWeight || currentStyles?.weight || ''} 
                            onChange={e => {
                                const val = e.target.value;
                                if (editorRef.current) {
                                    editorRef.current.style.fontWeight = val;
                                    if (val) {
                                        editorRef.current.querySelectorAll('b, strong, [style*="font-weight"]').forEach(el => {
                                            el.style.fontWeight = '';
                                            if (el.tagName === 'B' || el.tagName === 'STRONG') {
                                                const parent = el.parentNode;
                                                while (el.firstChild) parent.insertBefore(el.firstChild, el);
                                                parent.removeChild(el);
                                            }
                                        });
                                    }
                                }
                                if (onStyleChange) {
                                    onStyleChange('FontWeight', val);
                                    onStyleChange('Weight', val);
                                }
                            }}
                            title="Font Weight"
                            style={{ height: '32px', fontSize: '0.8rem', padding: '0 4px', border: '1px solid #3b82f6', borderRadius: '4px', background: 'white', cursor: 'pointer', boxSizing: 'border-box', margin: 0, outline: 'none' }}
                        >
                            <option value="">Weight</option>
                            <option value="100">100 Thin</option>
                            <option value="200">200 Extra Light</option>
                            <option value="300">300 Light</option>
                            <option value="400">400 Normal</option>
                            <option value="500">500 Medium</option>
                            <option value="600">600 Semi</option>
                            <option value="700">700 Bold</option>
                            <option value="800">800 Extra</option>
                            <option value="900">900 Black</option>
                        </select>

                        {/* Bold Button */}
                        <button 
                            type="button" 
                            {...makeBtnProps(() => {
                                restoreSelection();
                                const sel = window.getSelection();
                                const hasSelection = sel && sel.rangeCount > 0 && !sel.getRangeAt(0).collapsed;
                                if (hasSelection) {
                                    exec('bold');
                                } else {
                                    const curWeight = currentStyles?.fontWeight || currentStyles?.weight || (editorRef.current ? window.getComputedStyle(editorRef.current).fontWeight : '400');
                                    const isBold = curWeight === 'bold' || curWeight === '700' || curWeight === '800' || curWeight === '900' || parseInt(curWeight) >= 700;
                                    const nextWeight = isBold ? '400' : '700';
                                    if (editorRef.current) editorRef.current.style.fontWeight = nextWeight;
                                    if (onStyleChange) {
                                        onStyleChange('FontWeight', nextWeight);
                                        onStyleChange('Weight', nextWeight);
                                    }
                                }
                            })} 
                            title="Bold (B)"
                            style={{ flexShrink: 0, fontWeight: 'bold', width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #3b82f6', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            B
                        </button>

                        {/* Italic Button */}
                        <button 
                            type="button" 
                            {...makeBtnProps(() => {
                                restoreSelection();
                                const sel = window.getSelection();
                                const hasSelection = sel && sel.rangeCount > 0 && !sel.getRangeAt(0).collapsed;
                                if (hasSelection) {
                                    exec('italic');
                                } else {
                                    const curStyle = currentStyles?.fontStyle || (editorRef.current ? window.getComputedStyle(editorRef.current).fontStyle : 'normal');
                                    const nextStyle = curStyle === 'italic' ? 'normal' : 'italic';
                                    if (editorRef.current) editorRef.current.style.fontStyle = nextStyle;
                                    if (onStyleChange) {
                                        onStyleChange('FontStyle', nextStyle);
                                    }
                                }
                            })} 
                            title="Italic (I)"
                            style={{ flexShrink: 0, fontStyle: 'italic', width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #3b82f6', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            I
                        </button>

                        {/* Underline Button */}
                        <button 
                            type="button" 
                            {...makeBtnProps(() => {
                                restoreSelection();
                                const sel = window.getSelection();
                                const hasSelection = sel && sel.rangeCount > 0 && !sel.getRangeAt(0).collapsed;
                                if (hasSelection) {
                                    exec('underline');
                                } else {
                                    const curDec = currentStyles?.textDecoration || (editorRef.current ? (window.getComputedStyle(editorRef.current).textDecorationLine || window.getComputedStyle(editorRef.current).textDecoration) : 'none');
                                    const nextDec = curDec?.includes('underline') ? 'none' : 'underline';
                                    if (editorRef.current) editorRef.current.style.textDecoration = nextDec;
                                    if (onStyleChange) {
                                        onStyleChange('TextDecoration', nextDec);
                                    }
                                }
                            })} 
                            title="Underline (U)"
                            style={{ flexShrink: 0, textDecoration: 'underline', width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #3b82f6', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            U
                        </button>

                        {/* Clear Formatting Button */}
                        <button 
                            type="button" 
                            {...makeBtnProps((e) => {
                                removeFormatCustom(e);
                                if (editorRef.current) {
                                    editorRef.current.style.fontWeight = '';
                                    editorRef.current.style.fontStyle = '';
                                    editorRef.current.style.textDecoration = '';
                                    editorRef.current.style.color = '';
                                    editorRef.current.style.fontSize = '';
                                    editorRef.current.style.textTransform = '';
                                    editorRef.current.querySelectorAll('font[color], span[style*="color"]').forEach(el => {
                                        el.style.color = '';
                                        el.removeAttribute('color');
                                    });
                                }
                                if (onStyleChange) {
                                    onStyleChange('FontWeight', '');
                                    onStyleChange('Weight', '');
                                    onStyleChange('FontStyle', '');
                                    onStyleChange('TextDecoration', '');
                                    onStyleChange('Color', '');
                                    onStyleChange('Size', '');
                                    onStyleChange('Transform', 'none');
                                    onStyleChange('TextTransform', 'none');
                                    onStyleChange('TextGradientMode', 'solid');
                                }
                            })} 
                            title="Clear Formatting" 
                            style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '4px', color: '#ef4444', fontWeight: 'bold', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            Tx
                        </button>

                        <select 
                            onFocus={saveSelection}
                            onTouchStart={saveSelection}
                            value={currentStyles?.align || ''} 
                            onChange={e => {
                                const val = e.target.value;
                                if (editorRef.current) {
                                    editorRef.current.style.textAlign = val;
                                    editorRef.current.style.width = '100%';
                                    if (val === 'justify') editorRef.current.style.display = 'block';
                                }
                                if (onStyleChange) onStyleChange('Align', val);
                            }}
                            style={{ height: '32px', fontSize: '0.8rem', padding: '0 4px', border: '1px solid #3b82f6', borderRadius: '4px', background: 'white', cursor: 'pointer', boxSizing: 'border-box', margin: 0, outline: 'none' }}
                        >
                            <option value="">Align</option><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option><option value="justify">Justify</option>
                        </select>
                        <input 
                            onFocus={saveSelection}
                            onTouchStart={saveSelection}
                            placeholder="Size" 
                            title="Font Size (e.g. 24px, 1.5rem, 18)"
                            value={currentStyles?.size || ''} 
                            onChange={e => {
                                const val = e.target.value;
                                applyCustomFontSize(val, true);
                                if (onStyleChange) {
                                    onStyleChange('Size', val);
                                }
                            }}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    restoreSelection();
                                }
                            }}
                            style={{ width: '60px', height: '32px', fontSize: '0.8rem', padding: '0 6px', border: '1px solid #3b82f6', borderRadius: '4px', boxSizing: 'border-box', margin: 0, outline: 'none' }} 
                        />

                        {/* Text Transform Dropdown */}
                        <select 
                            onFocus={saveSelection}
                            onTouchStart={saveSelection}
                            value={currentStyles?.transform || currentStyles?.textTransform || effectiveStyle?.textTransform || 'none'} 
                            onChange={e => {
                                const val = e.target.value;
                                if (editorRef.current) {
                                    editorRef.current.style.textTransform = val;
                                }
                                if (onStyleChange) {
                                    onStyleChange('Transform', val);
                                    onStyleChange('TextTransform', val);
                                }
                            }}
                            title="Text Transform (Normal, UPPERCASE, lowercase, Capitalize)"
                            style={{ height: '32px', fontSize: '0.8rem', padding: '0 4px', border: '1px solid #3b82f6', borderRadius: '4px', background: 'white', cursor: 'pointer', boxSizing: 'border-box', margin: 0, outline: 'none' }}
                        >
                            <option value="none">Transform: Normal</option>
                            <option value="uppercase">UPPERCASE</option>
                            <option value="lowercase">lowercase</option>
                            <option value="capitalize">Capitalize</option>
                        </select>

                        {/* Colour Mode Switcher */}
                        <select
                            onFocus={saveSelection}
                            onTouchStart={saveSelection}
                            value={currentStyles?.textGradientMode || 'solid'}
                            onChange={e => {
                                const mode = e.target.value;
                                if (mode === 'gradient' && editorRef.current) {
                                    editorRef.current.querySelectorAll('font[color], span[style*="color"]').forEach(el => {
                                        el.style.color = '';
                                        el.removeAttribute('color');
                                    });
                                }
                                onStyleChange('TextGradientMode', mode);
                            }}
                            style={{ height: '32px', fontSize: '0.8rem', padding: '0 4px', border: '1px solid #3b82f6', borderRadius: '4px', background: 'white', cursor: 'pointer', boxSizing: 'border-box', margin: 0, outline: 'none' }}
                            title="Text Colour Mode"
                        >
                            <option value="solid">Solid Colour</option>
                            <option value="gradient">Gradient Text</option>
                        </select>

                        {currentStyles?.textGradientMode === 'gradient' ? (
                            <div style={{ display: 'flex', alignItems: 'center', height: '32px', gap: '4px' }}>
                                {/* Gradient Start Colour */}
                                <div style={{ width: '28px', height: '32px', border: '1px solid #3b82f6', borderRadius: '4px', position: 'relative', overflow: 'hidden', background: currentStyles?.textGradientStart || '#3b82f6', boxSizing: 'border-box', flexShrink: 0 }} title="Gradient Start Colour">
                                    <input
                                        type="color"
                                        value={(currentStyles?.textGradientStart && currentStyles.textGradientStart.startsWith('#')) ? currentStyles.textGradientStart : '#3b82f6'}
                                        onChange={e => onStyleChange('TextGradientStart', e.target.value)}
                                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                                    />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Start #HEX"
                                    title="Gradient Start HEX Colour"
                                    value={currentStyles?.textGradientStart || ''}
                                    onFocus={saveSelection}
                                    onTouchStart={saveSelection}
                                    onChange={e => onStyleChange('TextGradientStart', e.target.value)}
                                    style={{ width: '70px', height: '32px', fontSize: '0.75rem', padding: '0 4px', border: '1px solid #3b82f6', borderRadius: '4px', boxSizing: 'border-box', outline: 'none' }}
                                />

                                {/* Gradient End Colour */}
                                <div style={{ width: '28px', height: '32px', border: '1px solid #3b82f6', borderRadius: '4px', position: 'relative', overflow: 'hidden', background: currentStyles?.textGradientEnd || '#9333ea', boxSizing: 'border-box', flexShrink: 0 }} title="Gradient End Colour">
                                    <input
                                        type="color"
                                        value={(currentStyles?.textGradientEnd && currentStyles.textGradientEnd.startsWith('#')) ? currentStyles.textGradientEnd : '#9333ea'}
                                        onChange={e => onStyleChange('TextGradientEnd', e.target.value)}
                                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                                    />
                                </div>
                                <input
                                    type="text"
                                    placeholder="End #HEX"
                                    title="Gradient End HEX Colour"
                                    value={currentStyles?.textGradientEnd || ''}
                                    onFocus={saveSelection}
                                    onTouchStart={saveSelection}
                                    onChange={e => onStyleChange('TextGradientEnd', e.target.value)}
                                    style={{ width: '70px', height: '32px', fontSize: '0.75rem', padding: '0 4px', border: '1px solid #3b82f6', borderRadius: '4px', boxSizing: 'border-box', outline: 'none' }}
                                />

                                {/* Gradient Angle */}
                                <input
                                    type="number"
                                    placeholder="Deg"
                                    title="Gradient Angle (degrees)"
                                    value={currentStyles?.textGradientAngle || '135'}
                                    onChange={e => onStyleChange('TextGradientAngle', e.target.value)}
                                    style={{ width: '45px', height: '32px', fontSize: '0.75rem', padding: '0 4px', border: '1px solid #3b82f6', borderRadius: '4px', boxSizing: 'border-box', outline: 'none' }}
                                />
                            </div>
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', height: '32px', gap: '4px' }}>
                                <div style={{ width: '32px', height: '32px', border: '1px solid #3b82f6', borderRadius: '4px', position: 'relative', overflow: 'hidden', background: currentStyles?.color || '#fff', boxSizing: 'border-box', flexShrink: 0 }} title="Heading Colour">
                                    <input 
                                        onFocus={saveSelection}
                                        onMouseDown={saveSelection}
                                        onTouchStart={saveSelection}
                                        type="color" 
                                        value={(currentStyles?.color && currentStyles.color.startsWith('#')) ? currentStyles.color : '#ffffff'} 
                                        onChange={e => {
                                            const val = e.target.value;
                                            restoreSelection();
                                            const sel = window.getSelection();
                                            const hasSelection = sel && sel.rangeCount > 0 && !sel.getRangeAt(0).collapsed;
                                            if (hasSelection) {
                                                exec('foreColor', val);
                                            } else if (editorRef.current) {
                                                editorRef.current.style.color = val;
                                                editorRef.current.querySelectorAll('font[color], span[style*="color"]').forEach(el => {
                                                    el.style.color = '';
                                                    el.removeAttribute('color');
                                                });
                                            }
                                            if (onStyleChange) {
                                                onStyleChange('Color', val);
                                            }
                                        }}
                                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} 
                                        title="Choose Heading Colour"
                                    />
                                </div>
                                <input
                                    type="text"
                                    placeholder="#HEX"
                                    title="Manual HEX Heading Colour Input"
                                    value={currentStyles?.color || ''}
                                    onFocus={saveSelection}
                                    onTouchStart={saveSelection}
                                    onChange={e => {
                                        const val = e.target.value;
                                        if (editorRef.current) {
                                            editorRef.current.style.color = val;
                                            editorRef.current.querySelectorAll('font[color], span[style*="color"]').forEach(el => {
                                                el.style.color = '';
                                                el.removeAttribute('color');
                                            });
                                        }
                                        if (onStyleChange) {
                                            onStyleChange('Color', val);
                                        }
                                    }}
                                    style={{ width: '75px', height: '32px', fontSize: '0.75rem', padding: '0 6px', border: '1px solid #3b82f6', borderRadius: '4px', boxSizing: 'border-box', outline: 'none' }}
                                />
                                {currentStyles?.color && (
                                    <button 
                                        type="button"
                                        onClick={() => {
                                            if (editorRef.current) {
                                                editorRef.current.style.color = '';
                                                editorRef.current.querySelectorAll('font[color], span[style*="color"]').forEach(el => {
                                                    el.style.color = '';
                                                    el.removeAttribute('color');
                                                });
                                            }
                                            onStyleChange('Color', '');
                                        }} 
                                        style={{ height: '32px', width: '32px', fontSize: '10px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ef4444', background: '#fee2e2', color: '#ef4444', borderRadius: '4px', boxSizing: 'border-box', margin: 0, padding: 0 }}
                                        title="Reset Heading Colour"
                                    >
                                        <LuX size={12} />
                                    </button>
                                )}
                            </div>
                        )}

                        <div style={{ flexShrink: 0, width: '1px', background: '#cbd5e1', margin: '0 2px', height: '22px' }}></div>
                        <button type="button" {...makeBtnProps(openLinkModal)} title="Add / Edit Link" style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #3b82f6', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb' }}><LuLink size={15} /></button>
                        <button type="button" {...makeBtnProps(removeLink)} title="Remove Link" style={{ flexShrink: 0, width: '32px', height: '32px', cursor: 'pointer', background: 'white', border: '1px solid #cbd5e1', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}><LuUnlink size={15} /></button>
                    </div>
                </DraggableToolbar>
            )}

            {renderLinkModal()}
            {renderVideoModal()}
            {renderFloatingVideoToolbar()}

            <MediaLibraryModal
                isOpen={showMediaLibraryModal}
                onClose={() => setShowMediaLibraryModal(false)}
                onSelect={handleSelectMediaFromLibrary}
                title="Insert Media from Uploads"
                allowedTypes={['image', 'video']}
            />

            <Tag
                className={className || props.className}
                data-editable={type}
                ref={editorRef}
                src={(Tag === 'img' || Tag === 'video') ? (props.src || resolveMediaUrl(localPreviewSrc || value)) : undefined}
                contentEditable={(Tag === 'img' || Tag === 'video' || !isEditing) ? undefined : true}
                suppressContentEditableWarning={true}
                onFocus={(e) => {
                    setIsActive(true);
                    saveSelection();
                    setTimeout(() => {
                        keepEditingPointInView({ smooth: true });
                    }, 150);
                    const text = e.currentTarget.textContent;
                    if (isPlaceholderText(text)) {
                        setTimeout(() => {
                            if (editorRef.current) {
                                const range = document.createRange();
                                range.selectNodeContents(editorRef.current);
                                const sel = window.getSelection();
                                sel.removeAllRanges();
                                sel.addRange(range);
                                saveSelection();
                            }
                        }, 10);
                    }
                }}
                onClickCapture={(e) => {
                    if (isEditing) {
                        const anchor = e.target?.closest ? e.target.closest('a') : null;
                        if (anchor) {
                            e.preventDefault();
                        }
                    }
                }}
                onClick={(e) => {
                    setIsActive(true);
                    saveSelection();
                    handleEditorClick(e);
                }}
                onTouchStart={() => setIsActive(true)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        setTimeout(() => {
                            saveSelection();
                            keepEditingPointInView({ smooth: true });
                        }, 20);
                    }
                    if (e.ctrlKey || e.metaKey || e.altKey) return;
                    const text = e.currentTarget.textContent;
                    if (isPlaceholderText(text)) {
                        e.currentTarget.innerHTML = '';
                    }
                }}
                onInput={(e) => {
                    if (Tag === 'img' || type === 'image') return;
                    isTypingRef.current = true;
                    saveSelection();
                    keepEditingPointInView({ smooth: false });
                    const clean = cleanAllLinks(e.currentTarget.innerHTML);
                    onChange({ target: { value: clean } });
                }}
                onBlur={(e) => {
                    saveSelection();
                    if (Tag === 'img' || type === 'image') return;
                    if (isEmptyHtml(e.currentTarget.innerHTML)) {
                        e.currentTarget.innerHTML = '';
                    }
                    onChange({ target: { value: cleanAllLinks(e.currentTarget.innerHTML) } });
                }}
                onMouseUp={() => {
                    saveSelection();
                    keepEditingPointInView({ smooth: false });
                }}
                onKeyUp={(e) => {
                    saveSelection();
                    keepEditingPointInView({ smooth: false });
                }}
                onTouchEnd={(e) => {
                    saveSelection();
                    setIsActive(true);
                    setTimeout(() => {
                        keepEditingPointInView({ smooth: true });
                    }, 200);
                }}
                onPaste={handlePaste}
                style={{
                    outline: 'none',
                    minWidth: '20px',
                    minHeight: (isEditing && Tag !== 'img' && Tag !== 'video' && isEmptyHtml(value)) ? '1.5em' : undefined,
                    cursor: isEditing ? 'text' : 'inherit',
                    WebkitUserSelect: isEditing ? 'text' : 'inherit',
                    userSelect: isEditing ? 'text' : 'inherit',
                    ...effectiveStyle
                }}
                {...props}
            />
        </div>
    );
};
export default Editable;
